/** Single scoring engine — shared by web, API, Android, autotrader, scan-full */
import { computeContextAwareRsiDivergence } from "./rsiScoring.js";
import { computeFibRetracementScore } from "./fibRetracementScore.js";
import { computeCoreBuyScoreV2, applyAddonCap, clampScore } from "./scoringV2.js";
import { computeTradeLevelsV2 } from "./targetsV2.js";
import { buildTradeSetupWinnerStory } from "./tradeSetupStory.js";
import { evaluateOptionsChecks } from "./optionsChecks.js";
import { attachScannerAction } from "./resolveScannerAction.js";
import { attachScanLanes } from "./scanLanes.js";
import { attachPrimeSetup } from "./primeSetupFlag.js";
import { calcGate, assertGateScoreConsistency } from "./gateCalc.js";
import { calcRsiMidlineScore } from "./rsiScoring.js";
import { evaluateConfluence } from "./confluenceScore.js";
import { computeMeasurementScoreBonus } from "./scanMeasurements.js";
import { computeMoveMetrics, generatePostMoveAssessment } from "./postMoveAssessmentCore.js";
import { emaLatest, smaLatest, attachMovingAverages } from "./movingAverages.js";

function calcExits(r) {
  const alerts = [];
  if (r.atrStop2x && r.price < r.atrStop2x) alerts.push({ level: "CRITICAL", msg: "Price broke ATR stop — exit rule triggered" });
  if (r.sellCount >= 4) alerts.push({ level: "CRITICAL", msg: `${r.sellCount}/5 sell conditions active` });
  if (r.rsiDiv?.bearish) alerts.push({ level: "HIGH", msg: "RSI bearish divergence" });
  if (r.volDiv?.bearish) alerts.push({ level: "HIGH", msg: "Volume divergence" });
  if (r.bb && r.price > r.bb.upper) alerts.push({ level: "MEDIUM", msg: "Extended above BB upper" });
  if (r.cmf != null && r.cmf < -0.10) alerts.push({ level: "MEDIUM", msg: "Heavy CMF distribution" });
  const level = alerts.some((a) => a.level === "CRITICAL")
    ? "CRITICAL"
    : alerts.some((a) => a.level === "HIGH")
      ? "HIGH"
      : alerts.some((a) => a.level === "MEDIUM")
        ? "MEDIUM"
        : "CLEAR";
  return { alerts, level };
}

const sma = (arr, n) => arr.length < n ? null : arr.slice(0, n).reduce((s, v) => s + v, 0) / n;

function emaArr(arr, n) {
  const rev = [...arr].reverse();
  if (rev.length < n) return arr.map(() => null);
  const k = 2 / (n + 1), out = Array(n - 1).fill(null);
  let e = rev.slice(0, n).reduce((s, v) => s + v, 0) / n; out.push(e);
  for (let i = n; i < rev.length; i++) { e = rev[i] * k + e * (1 - k); out.push(e); }
  return out.reverse();
}

function calcRSI(closes, n = 14) {
  const rev = [...closes].reverse();
  if (rev.length < n + 1) return null;
  let ag = 0, al = 0;
  for (let i = 1; i <= n; i++) { const d = rev[i] - rev[i - 1]; if (d > 0) ag += d; else al -= d; }
  ag /= n; al /= n;
  for (let i = n + 1; i < rev.length; i++) {
    const d = rev[i] - rev[i - 1];
    ag = (ag * (n - 1) + Math.max(d, 0)) / n;
    al = (al * (n - 1) + Math.max(-d, 0)) / n;
  }
  return al === 0 ? 100 : 100 - 100 / (1 + ag / al);
}

function calcMACD(closes) {
  const e12 = emaArr(closes, 12), e26 = emaArr(closes, 26);
  const ml = closes.map((_, i) => e12[i] != null && e26[i] != null ? e12[i] - e26[i] : null).filter(v => v != null);
  if (ml.length < 9) return null;
  const sig = emaArr(ml, 9);
  const hist0 = ml[0] - sig[0];
  const hist1 = ml[1] != null && sig[1] != null ? ml[1] - sig[1] : null;
  const hist2 = ml[2] != null && sig[2] != null ? ml[2] - sig[2] : null;
  // Histogram momentum: growing = accelerating in current direction, shrinking = losing steam
  const histGrowing  = hist1 != null && Math.abs(hist0) > Math.abs(hist1); // magnitude expanding
  const histShrinking = hist1 != null && Math.abs(hist0) < Math.abs(hist1); // magnitude contracting
  const histAccelBull = hist0 > 0 && histGrowing;   // bullish and getting stronger
  const histAccelBear = hist0 < 0 && histGrowing;   // bearish and getting stronger
  const histFadingBull = hist0 > 0 && histShrinking; // bullish but losing momentum
  const histFadingBear = hist0 < 0 && histShrinking; // bearish but losing momentum
  return {
    macd: ml[0], signal: sig[0], histogram: hist0,
    bullish: ml[0] > sig[0], crossover: ml[0] > sig[0] && ml[1] <= sig[1],
    crossunder: ml[0] < sig[0] && ml[1] >= sig[1],
    histGrowing, histShrinking, histAccelBull, histAccelBear, histFadingBull, histFadingBear,
  };
}

function calcBB(closes, n = 20) {
  if (closes.length < 60) return null;
  const bwOf = sl => { const m = sl.reduce((s, v) => s + v, 0) / sl.length; const sd = Math.sqrt(sl.reduce((s, v) => s + (v - m) ** 2, 0) / sl.length); return (4 * sd) / m; };
  const days = Math.min(closes.length, 126), bws = [];
  for (let i = 0; i + n <= days; i++) bws.push(bwOf(closes.slice(i, i + n)));
  const cur = bws[0], mn = Math.min(...bws), mx = Math.max(...bws);
  const m = sma(closes, n), sd = Math.sqrt(closes.slice(0, n).reduce((s, v) => s + (v - m) ** 2, 0) / n);

  // IV Rank proxy — where current BB width sits in its 126-day range (0-100)
  // Low ivRank = volatility compressed = cheap options = good time to buy
  // High ivRank = volatility expanded = expensive options = avoid buying premium
  const ivRank = mx > mn ? Math.round(((cur - mn) / (mx - mn)) * 100) : 50;
  const ivLabel = ivRank <= 20 ? "LOW" : ivRank <= 40 ? "BELOW AVG" : ivRank <= 60 ? "AVERAGE" : ivRank <= 80 ? "ELEVATED" : "HIGH";
  const ivColor = ivRank <= 20 ? "#22c55e" : ivRank <= 40 ? "#86efac" : ivRank <= 60 ? "#94a3b8" : ivRank <= 80 ? "#f97316" : "#ef4444";
  const goodForBuying = ivRank <= 35; // IV on sale — good to buy calls/puts
  const avoidBuying   = ivRank >= 70; // IV too expensive — consider spreads instead

  return {
    upper: m + 2 * sd, lower: m - 2 * sd, middle: m,
    isSqueeze: cur <= mn * 1.12, wasSqueezing: bws[1] != null && bws[1] <= mn * 1.12,
    ivRank, ivLabel, ivColor, goodForBuying, avoidBuying, curWidth: cur, minWidth: mn, maxWidth: mx,
  };
}

function calcCMF(h, l, c, v, n = 20) {
  if (c.length < n) return null;
  let mfv = 0, vol = 0;
  for (let i = 0; i < n; i++) { const hl = h[i] - l[i]; if (!hl) continue; mfv += ((c[i] - l[i]) - (h[i] - c[i])) / hl * v[i]; vol += v[i]; }
  return vol ? mfv / vol : 0;
}

function calcCMFDiv(h, l, c, v, lookback = 14) {
  // Need enough bars to compare two CMF windows
  if (c.length < lookback * 2) return null;

  // Calculate CMF for recent window (bars 0..lookback-1) and prior window (bars lookback..lookback*2-1)
  const recentCMF  = calcCMF(h.slice(0, lookback), l.slice(0, lookback), c.slice(0, lookback), v.slice(0, lookback), lookback);
  const priorCMF   = calcCMF(h.slice(lookback, lookback * 2), l.slice(lookback, lookback * 2), c.slice(lookback, lookback * 2), v.slice(lookback, lookback * 2), lookback);
  if (recentCMF == null || priorCMF == null) return null;

  // Price: compare recent high/low vs prior high/low to determine trend
  const recentHigh = Math.max(...c.slice(0, lookback));
  const priorHigh  = Math.max(...c.slice(lookback, lookback * 2));
  const recentLow  = Math.min(...c.slice(0, lookback));
  const priorLow   = Math.min(...c.slice(lookback, lookback * 2));

  // Min delta to avoid noise — price must move at least 1%, CMF at least 0.04
  const priceDelta = 0.01;
  const cmfDelta   = 0.04;

  const priceHigher = recentHigh > priorHigh * (1 + priceDelta);
  const priceLower  = recentLow  < priorLow  * (1 - priceDelta);
  const cmfHigher   = recentCMF  > priorCMF  + cmfDelta;
  const cmfLower    = recentCMF  < priorCMF  - cmfDelta;

  // Bearish divergence: price higher high but CMF lower high — rally losing fuel
  const bearishDiv = priceHigher && cmfLower && recentCMF < 0.1;

  // Bullish divergence: price lower low but CMF higher low — sellers exhausted
  const bullishDiv = priceLower && cmfHigher && recentCMF > -0.1;

  if (!bearishDiv && !bullishDiv) return null;

  return {
    bullishDiv,
    bearishDiv,
    recentCMF: parseFloat(recentCMF.toFixed(3)),
    priorCMF:  parseFloat(priorCMF.toFixed(3)),
    label: bullishDiv
      ? `CMF Bullish Divergence — price made lower low but CMF improved (${priorCMF.toFixed(3)} → ${recentCMF.toFixed(3)}). Sellers exhausting.`
      : `CMF Bearish Divergence — price made higher high but CMF weakened (${priorCMF.toFixed(3)} → ${recentCMF.toFixed(3)}). Rally losing fuel.`,
    tooltip: bullishDiv
      ? `Price hit a lower low but money flow improved — institutions buying into weakness. Early reversal signal.`
      : `Price hit a higher high but institutional buying is fading. Breakout may not be supported by real volume.`,
  };
}

function calcVWAP(h, l, c, v, n = 20) {
  let tpv = 0, vol = 0;
  for (let i = 0; i < Math.min(n, c.length); i++) { tpv += ((h[i] + l[i] + c[i]) / 3) * v[i]; vol += v[i]; }
  return vol ? tpv / vol : null;
}

function calcATR(highs, lows, closes, n = 14) {
  if (closes.length < n + 1) return null;
  const trs = [];
  for (let i = 0; i < n && i < closes.length - 1; i++)
    trs.push(Math.max(highs[i] - lows[i], Math.abs(highs[i] - closes[i + 1]), Math.abs(lows[i] - closes[i + 1])));
  if (trs.length < n) return null;
  return trs.reduce((s, v) => s + v, 0) / n;
}

function calcVolDiv(closes, volumes) {
  if (closes.length < 12 || volumes.length < 12) return null;
  const rc = closes.slice(0, 5), oc = closes.slice(5, 10);
  const rv = volumes.slice(0, 5), ov = volumes.slice(5, 10);
  const priceUp = Math.max(...rc) >= Math.max(...oc) * 0.98;
  const avgNew = rv.reduce((s, v) => s + v, 0) / rv.length;
  const avgOld = ov.reduce((s, v) => s + v, 0) / ov.length;
  const ratio = avgNew / avgOld;
  return { bearish: priceUp && ratio < 0.80, bullish: !priceUp && ratio > 1.20, ratio };
}

function calcFibs(closes, n = 63, highs = null, lows = null) {
  const len = Math.min(n, closes.length);
  const high = highs?.length ? Math.max(...highs.slice(0, len)) : Math.max(...closes.slice(0, len));
  const low = lows?.length ? Math.min(...lows.slice(0, len)) : Math.min(...closes.slice(0, len));
  const range = high - low;
  if (!range) return null;
  return {
    high, low, range,
    fib236: high - range * 0.236,
    fib382: high - range * 0.382,
    fib500: high - range * 0.500,
    fib618: high - range * 0.618,
    fib786: high - range * 0.786,
  };
}

function calcPivots(highs, lows, closes, n = 20) {
  if (highs.length < n) return null;
  const h = Math.max(...highs.slice(0, n)), l = Math.min(...lows.slice(0, n)), c = sma(closes, n);
  if (!c) return null;
  const p = (h + l + c) / 3;
  return { pivot: p, r1: 2 * p - l, r2: p + (h - l), s1: 2 * p - h, s2: p - (h - l) };
}

function calcRSIDiv(closes, highs = null, lows = null, context = {}) {
  return computeContextAwareRsiDivergence(closes, highs || closes, lows || closes, context);
}

function calc52w(closes) {
  const yr = closes.slice(0, Math.min(252, closes.length));
  if (yr.length < 20) return null;
  const high = Math.max(...yr), low = Math.min(...yr), price = closes[0], range = high - low;
  const score = range > 0 ? Math.round(((price - low) / range) * 100) : 50;
  return { high, low, score, nearLow: score <= 20, nearHigh: score >= 80, distFromLow: ((price - low) / low) * 100, distFromHigh: ((price - high) / high) * 100 };
}

// 52W Breakout Signal — replaces legacy "near 52W high" proximity logic.
// Returns a score adjustment ONLY; all other scoring logic stays unchanged.
function calc52wBreakoutSignalScore(range52w, rvol) {
  const pct = range52w?.score;
  if (pct == null) return 0;
  const ratio = rvol?.ratio;
  if (ratio == null) {
    // If we don't have volume context, keep neutral unless deep in range (<50%).
    return pct < 50 ? -1 : 0;
  }

  // Price AT 52W high (98–100%) rules (override the generic >90% band)
  if (pct >= 98) {
    if (ratio > 1.5) return 3;      // breakout in progress, highest bonus
    if (ratio < 1.0) return -1;     // stalling at high, no conviction
    if (ratio >= 1.0 && ratio <= 1.5) return 1.5; // approaching, building
    return 0;
  }

  // Price > 90% of 52W range rules
  if (pct > 90) {
    if (ratio > 1.5) return 2.5;    // confirmed breakout setup
    if (ratio >= 1.0 && ratio <= 1.5) return 1.5; // approaching, building
    return 0;                       // < 1.0x = no volume conviction
  }

  // Mid-range and deep-range structure rules
  if (pct < 50) return -1;          // deep in range, weak structure
  return 0;                         // 50–90% neutral
}

function calc52wBreakoutSetup({ range52w, rvol, cmf, buyScore, rsi, counterTrendWeeklyCut }) {
  const pct = range52w?.score;
  const ratio = rvol?.ratio;
  const has = {
    inTop10: pct != null && pct > 90,
    rvolConfirmed: ratio != null && ratio > 1.5,
    cmfConfirmed: cmf != null && cmf > 0.05,
    scoreConfirmed: buyScore != null && buyScore > 6,
    rsiOk: rsi != null && rsi >= 45 && rsi <= 75,
    weeklyOk: !counterTrendWeeklyCut,
  };
  const active = Object.values(has).every(Boolean);
  const topPct = pct != null ? Math.max(0, 100 - pct) : null; // "top X% of range"
  return { active, pct, topPct, ratio, checks: has };
}

function calc3Day(closes) {
  if (closes.length < 6) return { postDrop: null, breakout: null, earningsGap: null, gapFillWatch: null };
  let postDrop = null;
  for (let i = 0; i < 3; i++) {
    const d = ((closes[i] - closes[i + 1]) / closes[i + 1]) * 100;
    if (d <= -4) { postDrop = { daysAgo: i + 1, cooldownLeft: 3 - i, dropPct: d, active: (3 - i) > 0 }; break; }
  }
  let breakout = null;
  if (closes.length >= 25) {
    const res = Math.max(...closes.slice(1, 21));
    if (closes[0] > res) {
      const streak = [0, 1, 2].filter(i => closes[i] > res).length;
      breakout = { level: res, streak, confirmed: streak >= 3 };
    }
  }
  let earningsGap = null;
  for (let i = 0; i < 3; i++) {
    const g = ((closes[i] - closes[i + 1]) / closes[i + 1]) * 100;
    if (Math.abs(g) >= 5) { earningsGap = { daysAgo: i + 1, pct: g, active: (3 - i) > 0, stabilLeft: 3 - i }; break; }
  }
  // Gap Fill Watch — bullish earnings gap (5%+) within last 5 days
  // Stocks that gap up on earnings typically drift higher for 3-5 sessions before retracing
  // Optimal entry: gap day close or first pullback to gap day close price
  let gapFillWatch = null;
  for (let i = 0; i < 5; i++) {
    if (i + 1 >= closes.length) break;
    const g = ((closes[i] - closes[i + 1]) / closes[i + 1]) * 100;
    if (g >= 5) {
      const daysAgo = i + 1;
      const gapPrice = closes[i + 1]; // price before gap (entry zone)
      const currentPrice = closes[0];
      const driftPct = ((currentPrice - closes[i]) / closes[i]) * 100; // drift since gap close
      const windowOpen = daysAgo <= 5; // still in drift window
      gapFillWatch = {
        daysAgo,
        gapPct: g,
        gapPrice,       // support level — the pre-gap close
        driftPct,       // how much it's moved since the gap
        windowOpen,     // still within 5-session drift window
        active: windowOpen && driftPct > -3, // not if it's already retracing hard
      };
      break;
    }
  }
  return { postDrop, breakout, earningsGap, gapFillWatch };
}

// ADX — Average Directional Index (trend strength, 14-period)
function calcADX(highs, lows, closes, n = 14) {
  if (closes.length < n + 2) return null;
  const trs = [], pdms = [], ndms = [];
  for (let i = 0; i < closes.length - 1; i++) {
    const h = highs[i], l = lows[i], ph = highs[i + 1], pl = lows[i + 1], pc = closes[i + 1];
    trs.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
    pdms.push(h - ph > pl - l && h - ph > 0 ? h - ph : 0);
    ndms.push(pl - l > h - ph && pl - l > 0 ? pl - l : 0);
  }
  const atr14 = trs.slice(0, n).reduce((s, v) => s + v, 0) / n;
  const pdi14 = (pdms.slice(0, n).reduce((s, v) => s + v, 0) / n) / atr14 * 100;
  const ndi14 = (ndms.slice(0, n).reduce((s, v) => s + v, 0) / n) / atr14 * 100;
  const dx = Math.abs(pdi14 - ndi14) / (pdi14 + ndi14) * 100;
  // Approximate ADX as smoothed DX
  const dxArr = [];
  for (let i = 0; i + n < trs.length; i++) {
    const atr = trs.slice(i, i + n).reduce((s, v) => s + v, 0) / n;
    const pdi = (pdms.slice(i, i + n).reduce((s, v) => s + v, 0) / n) / atr * 100;
    const ndi = (ndms.slice(i, i + n).reduce((s, v) => s + v, 0) / n) / atr * 100;
    dxArr.push(Math.abs(pdi - ndi) / (pdi + ndi + 0.001) * 100);
  }
  const adx = dxArr.slice(0, n).reduce((s, v) => s + v, 0) / n;
  return {
    adx, pdi: pdi14, ndi: ndi14,
    strong: adx >= 25,
    trending: adx >= 20,
    ranging: adx < 20,
    label: adx >= 40 ? "VERY STRONG" : adx >= 25 ? "STRONG TREND" : adx >= 20 ? "DEVELOPING" : "RANGING / CHOPPY",
    color: adx >= 25 ? "#22c55e" : adx >= 20 ? "#eab308" : "#ef4444",
  };
}

// Stochastic Oscillator (14, 3, 3)
function calcStoch(highs, lows, closes, kPeriod = 14, dPeriod = 3) {
  if (closes.length < kPeriod + dPeriod) return null;
  const kArr = [];
  for (let i = 0; i + kPeriod <= closes.length; i++) {
    const hSlice = highs.slice(i, i + kPeriod);
    const lSlice = lows.slice(i, i + kPeriod);
    const h = Math.max(...hSlice), l = Math.min(...lSlice);
    kArr.push(h === l ? 50 : ((closes[i] - l) / (h - l)) * 100);
  }
  const kSmooth = kArr.slice(0, dPeriod).reduce((s, v) => s + v, 0) / dPeriod; // %K smoothed
  const dLine = kArr.slice(0, dPeriod * 2).reduce((s, v) => s + v, 0) / (dPeriod * 2); // %D approx
  const prevK = kArr.slice(1, dPeriod + 1).reduce((s, v) => s + v, 0) / dPeriod;
  return {
    k: kSmooth, d: dLine,
    overbought: kSmooth > 80,
    oversold: kSmooth < 20,
    bullCross: kSmooth > dLine && prevK <= dLine,
    bearCross: kSmooth < dLine && prevK >= dLine,
    label: kSmooth > 80 ? "OVERBOUGHT" : kSmooth < 20 ? "OVERSOLD" : "NEUTRAL",
  };
}

// On Balance Volume
function calcOBV(closes, volumes) {
  if (closes.length < 10) return null;
  let obv = 0;
  const obvArr = [0];
  for (let i = 0; i < closes.length - 1; i++) {
    if (closes[i] > closes[i + 1]) obv += volumes[i];
    else if (closes[i] < closes[i + 1]) obv -= volumes[i];
    obvArr.push(obv);
  }
  // OBV trend: is OBV rising while price rises? (confirmation) or diverging?
  const recentOBV = obvArr.slice(0, 5).reduce((s, v) => s + v, 0) / 5;
  const priorOBV = obvArr.slice(5, 15).reduce((s, v) => s + v, 0) / 10;
  const recentPrice = closes.slice(0, 5).reduce((s, v) => s + v, 0) / 5;
  const priorPrice = closes.slice(5, 15).reduce((s, v) => s + v, 0) / 10;
  const obvRising = recentOBV > priorOBV;
  const priceRising = recentPrice > priorPrice;
  return {
    value: obv,
    rising: obvRising,
    confirming: obvRising === priceRising,
    bullishDiv: !priceRising && obvRising,   // price down, OBV up = hidden strength
    bearishDiv: priceRising && !obvRising,   // price up, OBV down = weak move
    label: obvRising ? "RISING" : "FALLING",
  };
}

function calcParabolicSAR(highs, lows, closes, af = 0.02, maxAF = 0.2) {
  if (closes.length < 10) return null;
  const rev = { h: [...highs].reverse(), l: [...lows].reverse(), c: [...closes].reverse() };
  let bull = rev.c[1] < rev.c[0]; // initial trend
  let sar = bull ? rev.l[0] : rev.h[0];
  let ep = bull ? rev.h[0] : rev.l[0];
  let curAF = af;

  for (let i = 1; i < Math.min(rev.c.length - 1, 30); i++) {
    sar = sar + curAF * (ep - sar);
    if (bull) {
      if (rev.l[i] < sar) { bull = false; sar = ep; ep = rev.l[i]; curAF = af; }
      else { if (rev.h[i] > ep) { ep = rev.h[i]; curAF = Math.min(curAF + af, maxAF); } }
    } else {
      if (rev.h[i] > sar) { bull = true; sar = ep; ep = rev.h[i]; curAF = af; }
      else { if (rev.l[i] < ep) { ep = rev.l[i]; curAF = Math.min(curAF + af, maxAF); } }
    }
  }
  return {
    sar, bullish: bull,
    label: bull ? "BULLISH (SAR below price)" : "BEARISH (SAR above price)",
    stopLevel: sar,
  };
}

// Relative Volume (RVOL) — today vs 20-day avg
function calcRVOL(volumes, n = 20) {
  if (volumes.length < n + 1) return null;
  const avgVol = volumes.slice(1, n + 1).reduce((s, v) => s + v, 0) / n;
  const todayVol = volumes[0];
  const ratio = avgVol > 0 ? todayVol / avgVol : 1;
  return {
    today: todayVol, avg: avgVol, ratio,
    surge: ratio >= 2.0,
    high: ratio >= 1.5,
    normal: ratio >= 0.7 && ratio < 1.5,
    low: ratio < 0.7,
    label: ratio >= 2.0 ? "SURGE (2x+)" : ratio >= 1.5 ? "HIGH (1.5x+)" : ratio >= 0.7 ? "NORMAL" : "LOW",
    color: ratio >= 2.0 ? "#22c55e" : ratio >= 1.5 ? "#06b6d4" : ratio < 0.7 ? "#ef4444" : "#94a3b8",
  };
}


// Williams %R — momentum oscillator, excellent for mean reversion -80 to -20 transitions
function calcWilliamsR(highs, lows, closes, n = 14) {
  if (closes.length < n) return null;
  const highN = Math.max(...highs.slice(0, n));
  const lowN  = Math.min(...lows.slice(0, n));
  const range = highN - lowN;
  if (range === 0) return null;
  const R = ((highN - closes[0]) / range) * -100; // 0 to -100
  // Previous reading for transition detection
  const highP = highs.length >= n + 1 ? Math.max(...highs.slice(1, n + 1)) : highN;
  const lowP  = lows.length  >= n + 1 ? Math.min(...lows.slice(1, n + 1))  : lowN;
  const rangeP = highP - lowP;
  const Rprev  = rangeP > 0 ? ((highP - closes[1]) / rangeP) * -100 : R;
  const oversold   = R <= -80;
  const overbought = R >= -20;
  const prevOversold   = Rprev <= -80;
  const prevOverbought = Rprev >= -20;
  // Key transitions: crossing OUT of oversold (-80→-20) = bullish, OUT of overbought = bearish
  const bullCross = prevOversold && !oversold;   // crossed above -80 from below (mean reversion buy)
  const bearCross = prevOverbought && !overbought; // crossed below -20 from above (reversal sell)
  return {
    value: Math.round(R * 10) / 10,
    oversold, overbought, bullCross, bearCross,
    label: oversold ? "OVERSOLD" : overbought ? "OVERBOUGHT" : "NEUTRAL",
    color: oversold ? "#22c55e" : overbought ? "#ef4444" : "#94a3b8",
    tooltip: `Williams %R: ${Math.round(R)}. Range -100 to 0. Below -80 = oversold. Above -20 = overbought. The most powerful signal is the TRANSITION — when %R crosses from -80 back toward -20, price has found a floor and momentum is recovering.`,
  };
}

// Volume Breakout Confirmation — validates breakouts vs fake-outs
function calcVolumeBreakout(closes, highs, lows, volumes, bb, ema21) {
  if (!volumes || volumes.length < 20 || !closes.length) return null;
  const price = closes[0];
  const avgVol20 = volumes.slice(1, 21).reduce((s, v) => s + v, 0) / 20;
  const todayVol = volumes[0];
  const volRatio = avgVol20 > 0 ? todayVol / avgVol20 : 1;
  // Detect breakout scenarios
  const breakAboveBB    = bb && price > bb.upper;
  const breakAboveEMA21 = ema21 && closes[1] < ema21 && price >= ema21; // yesterday below, today above
  const breakAbove52W20 = highs.length >= 20 && price >= Math.max(...highs.slice(1, 20)); // new 20-day high
  const isBreakout      = breakAboveBB || breakAboveEMA21 || breakAbove52W20;
  // Volume thresholds for breakout validation
  const volConfirmed = isBreakout && volRatio >= 1.5;  // breakout WITH volume — valid
  const volFakeout   = isBreakout && volRatio < 0.85;  // breakout WITHOUT volume — suspect
  const volSurgeNoBreakout = !isBreakout && volRatio >= 2.0; // volume surge but no breakout — watch
  return {
    isBreakout, volConfirmed, volFakeout, volSurgeNoBreakout,
    breakType: breakAboveEMA21 ? "EMA21 Cross" : breakAboveBB ? "BB Upper Break" : breakAbove52W20 ? "20D High Break" : null,
    volRatio: Math.round(volRatio * 100) / 100,
    label: volConfirmed ? "CONFIRMED" : volFakeout ? "FAKEOUT RISK" : volSurgeNoBreakout ? "VOL SURGE" : isBreakout ? "BREAKOUT" : "—",
    color: volConfirmed ? "#22c55e" : volFakeout ? "#ef4444" : volSurgeNoBreakout ? "#eab308" : "#94a3b8",
    tooltip: `Volume breakout validation. ${isBreakout ? `Breakout detected (${breakAboveEMA21 ? "EMA21 cross" : breakAboveBB ? "above BB upper" : "20-day high"}). Volume is ${volRatio.toFixed(1)}x average — ${volConfirmed ? "CONFIRMED: volume supports the breakout." : "FAKEOUT RISK: low volume on breakout is a warning sign — often fails."}` : volSurgeNoBreakout ? `Volume spike ${volRatio.toFixed(1)}x average without a breakout — potential accumulation or news.` : `No breakout. Volume ${volRatio.toFixed(1)}x normal.`}`,
  };
}

// Higher Highs / Higher Lows — trend structure
function calcTrendStructure(highs, lows, closes, n = 10) {
  if (closes.length < n * 2) return null;
  const recent = { h: Math.max(...highs.slice(0, n)), l: Math.min(...lows.slice(0, n)) };
  const prior = { h: Math.max(...highs.slice(n, n * 2)), l: Math.min(...lows.slice(n, n * 2)) };
  const hh = recent.h > prior.h; // higher high
  const hl = recent.l > prior.l; // higher low
  const lh = recent.h < prior.h; // lower high
  const ll = recent.l < prior.l; // lower low
  let structure, color;
  if (hh && hl) { structure = "UPTREND (HH+HL)"; color = "#22c55e"; }
  else if (lh && ll) { structure = "DOWNTREND (LH+LL)"; color = "#ef4444"; }
  else if (hh && ll) { structure = "EXPANDING"; color = "#eab308"; }
  else if (lh && hl) { structure = "CONTRACTING"; color = "#06b6d4"; }
  else { structure = "MIXED"; color = "#94a3b8"; }
  return { hh, hl, lh, ll, structure, color, bullish: hh && hl, bearish: lh && ll };
}

const isPureCrypto = sym => typeof sym === "string" && sym.endsWith("-USD"); // spot crypto only — no earnings, no SPY RS


// ─── WEEKLY TREND (resampled from daily bars — no extra API call) ──────────────
// Takes daily closes array and computes weekly EMA21 + trend direction
// Used to filter out setups where daily is bullish but weekly is bearish
function calcWeeklyTrend(closes) {
  if (!closes || closes.length < 40) return null;
  // Resample: take every 5th bar as weekly close (approx)
  const weekly = [];
  for (let i = 0; i < closes.length - 4; i += 5) {
    weekly.push(closes[i]); // most recent close of each ~week
  }
  if (weekly.length < 10) return null;
  const wEma21 = emaArr(weekly, Math.min(21, weekly.length - 1))[0];
  const wEma8  = emaArr(weekly, Math.min(8,  weekly.length - 1))[0];
  const wPrice = weekly[0];
  if (!wEma21 || !wEma8) return null;
  const aboveEma21 = wPrice > wEma21;
  const aboveEma8  = wPrice > wEma8;
  const ema8AboveEma21 = wEma8 > wEma21;
  // Weekly trend strength
  const strongUptrend  = aboveEma21 && aboveEma8 && ema8AboveEma21;
  const weakUptrend    = aboveEma21 && !aboveEma8;
  const downtrend      = !aboveEma21;
  const label = strongUptrend ? "STRONG UP" : weakUptrend ? "WEAK UP" : "DOWN";
  const wMacd = weekly.length >= 35 ? calcMACD(weekly) : null;
  const weeklyBullish = aboveEma21;
  const weeklyBearish = !aboveEma21 && downtrend;
  const weeklyNeutral = !weeklyBullish && !weeklyBearish;
  return { wPrice, wEma21, wEma8, aboveEma21, aboveEma8, ema8AboveEma21, strongUptrend, weakUptrend, downtrend, label, wMacd, weeklyBullish, weeklyBearish, weeklyNeutral };
}

function bbBandwidthAtOffset(closes, offset, n = 20) {
  const sl = closes.slice(offset, offset + n);
  if (sl.length < n) return null;
  const m = sl.reduce((s, v) => s + v, 0) / n;
  const sd = Math.sqrt(sl.reduce((s, v) => s + (v - m) ** 2, 0) / n);
  return m > 0 ? (4 * sd) / m : null;
}

function cmfAtOffset(highs, lows, closes, volumes, offset, win = 20) {
  return calcCMF(
    highs.slice(offset),
    lows.slice(offset),
    closes.slice(offset),
    volumes.slice(offset),
    win
  );
}

function obvCumulativeLF(closes, volumes) {
  if (!closes?.length) return null;
  let v = 0;
  const arr = [0];
  for (let i = 0; i < closes.length - 1; i++) {
    if (closes[i] > closes[i + 1]) v += volumes[i] || 0;
    else if (closes[i] < closes[i + 1]) v -= volumes[i] || 0;
    arr.push(v);
  }
  return arr;
}

function rvolAtOffset(volumes, offset, n = 20) {
  if (!volumes?.length || volumes.length < offset + n + 1) return null;
  const avg = volumes.slice(offset + 1, offset + n + 1).reduce((s, x) => s + (x || 0), 0) / n;
  const today = volumes[offset] || 0;
  return avg > 0 ? today / avg : null;
}

const SECTOR_ETF_NAMES = { XLK: "Tech", XLC: "Comm Services", XLY: "Discretionary", IGV: "Software", XLF: "Financials", XLV: "Healthcare", XLE: "Energy", XLI: "Industrials", XLRE: "Real Estate", SMH: "Semis", SOXX: "Semis" };

function computeEarlyWarningPhases(ctx) {
  const {
    closes, highs, lows, volumes, bb, pivots, range52w, cmf, obv, rvol, rs, rsVsSector,
    symbol, optionsFlow, phaseContext, pureCrypto, price, stockReturn63,
  } = ctx;

  const empty = {
    phaseScore: 0,
    phase1Bull: false,
    phase1Bear: false,
    phase2Bull: false,
    phase2Bear: false,
    phase1Any: false,
    phase2Any: false,
    labels: [],
    timeline: [],
    timelineLines: [],
    signals: {},
    sectorLeadBull: false,
    sectorLeadBear: false,
    progressionNote: null,
  };
  if (pureCrypto || !closes || closes.length < 65) return empty;

  const labels = [];
  let score = 0;
  const timeline = [];
  const signals = {};
  const breakdown = {
    accumulation: 0,
    rsLeader: 0,
    distribution: 0,
    preBreakout: 0,
    optionsFlowShift: 0,
    preBreakdown: 0,
    sectorRotation: 0,
  };

  const cmf0 = cmf;
  const cmf1 = cmfAtOffset(highs, lows, closes, volumes, 1);
  const cmf2 = cmfAtOffset(highs, lows, closes, volumes, 2);
  const cmf3 = cmfAtOffset(highs, lows, closes, volumes, 3);
  const cmfRising3 = cmf0 != null && cmf1 != null && cmf2 != null && cmf3 != null && cmf0 > cmf1 && cmf1 > cmf2 && cmf2 > cmf3;
  const cmfFalling3 = cmf0 != null && cmf1 != null && cmf2 != null && cmf3 != null && cmf0 < cmf1 && cmf1 < cmf2 && cmf2 < cmf3;

  const obvLF = obvCumulativeLF(closes, volumes);
  const obvHigherLows7 = (() => {
    if (!obvLF || obvLF.length < 15) return false;
    const a = Math.min(...obvLF.slice(0, 7));
    const b = Math.min(...obvLF.slice(7, 14));
    return a > b;
  })();
  const obvLowerHighs7 = (() => {
    if (!obvLF || obvLF.length < 15) return false;
    const a = Math.max(...obvLF.slice(0, 7));
    const b = Math.max(...obvLF.slice(7, 14));
    return a < b;
  })();

  const price5d = closes[5] ? ((closes[0] - closes[5]) / closes[5]) * 100 : null;
  const price3d = closes[3] ? ((closes[0] - closes[3]) / closes[3]) * 100 : null;
  const priceFlatOrDown = price5d != null && price5d <= 0.6;
  const priceFlatOrUp = price5d != null && price5d >= -0.6;

  let upVol = 0, downVol = 0;
  for (let i = 0; i < 7; i++) {
    const c0 = closes[i], c1 = closes[i + 1];
    const v0 = volumes[i] || 0;
    if (c0 < c1) downVol += v0;
    else if (c0 > c1) upVol += v0;
  }
  const volBullRatio = downVol > 0 ? upVol / downVol : (upVol > 0 ? 99 : 0);
  const volBearRatio = upVol > 0 ? downVol / upVol : (downVol > 0 ? 99 : 0);

  const bws = [];
  for (let i = 0; i < 25; i++) bws.push(bbBandwidthAtOffset(closes, i, 20));
  const bbNarrow5 = bws[0] != null && bws[1] != null && bws[2] != null && bws[3] != null && bws[4] != null
    && bws[0] < bws[1] && bws[1] < bws[2] && bws[2] < bws[3] && bws[3] < bws[4];
  const bbNarrow5Bear = bbNarrow5;

  const accumulation = cmfRising3 && cmf0 < 0.05 && obvHigherLows7 && priceFlatOrDown && volBullRatio > 1.3 && bbNarrow5;
  if (accumulation) {
    score += 2;
    breakdown.accumulation += 2;
    labels.push("🔍 ACCUMULATION DETECTED");
    signals.accumulation = true;
    timeline.push({ phase: 1, text: "Phase 1: ✅ Accumulation pattern active", ok: true });
  } else {
    timeline.push({ phase: 1, text: "Phase 1: ⏳ Accumulation not fully aligned (CMF/OBV/vol/BB width)", ok: false });
    signals.accumulation = false;
  }

  const spy5d = phaseContext?.spy5dPct;
  const stock5dPct = closes[5] ? ((closes[0] - closes[5]) / closes[5]) * 100 : null;
  const spy5dOld = phaseContext?.spy5dPctAt5 ?? null;
  const rsSpreadNow = stock5dPct != null && spy5d != null ? stock5dPct - spy5d : null;
  const rsSpreadThen = stock5dPct != null && spy5dOld != null ? (closes[5] && closes[10] ? (((closes[5] - closes[10]) / closes[10]) * 100) - spy5dOld : null) : null;
  const rsImproving5 = rsSpreadNow != null && rsSpreadThen != null && rsSpreadNow > rsSpreadThen + 0.15;
  const sector3d = phaseContext?.sector3dPct;
  const sectorFlatOrDown = sector3d == null || sector3d <= 0.35;
  const rsLeader = rsImproving5 && stock5dPct != null && stock5dPct <= 0.4 && sectorFlatOrDown;
  if (rsLeader) {
    score += 2;
    breakdown.rsLeader += 2;
    labels.push("💪 RS LEADER — Holding vs sector weakness");
    signals.rsLeader = true;
  } else {
    signals.rsLeader = false;
  }

  const distribution = cmfFalling3 && cmf0 > -0.05 && obvLowerHighs7 && priceFlatOrUp && volBearRatio > 1.3 && bbNarrow5Bear;
  if (distribution) {
    score -= 2;
    breakdown.distribution -= 2;
    labels.push("🔍 DISTRIBUTION DETECTED");
    signals.distribution = true;
    timeline.push({ phase: 1, text: "Phase 1: ✅ Distribution pressure building", ok: true });
  } else {
    signals.distribution = false;
    if (!accumulation) timeline.push({ phase: 1, text: "Phase 1: ⏳ No distribution cluster", ok: false });
  }

  const bws20 = bws.slice(0, 20).filter(v => v != null);
  const tightestSqueeze = bws20.length >= 10 && bws[0] != null && Math.abs(bws[0] - Math.min(...bws20)) < 1e-8;

  const resistance = pivots?.r1 && range52w?.high
    ? (Math.abs(price - pivots.r1) / pivots.r1 < Math.abs(price - range52w.high) / range52w.high ? pivots.r1 : range52w.high)
    : (pivots?.r1 || range52w?.high);
  const nearRes3 = resistance ? [0, 1, 2].every(i => {
    const p = closes[i];
    return Math.abs(p - resistance) / resistance < 0.011;
  }) : false;

  const rvolTrendUp5 = [0, 1, 2, 3, 4].every(i => {
    const a = rvolAtOffset(volumes, i);
    const b = rvolAtOffset(volumes, i + 1);
    return a != null && b != null && a > b * 0.998;
  });
  const cmfCrossUp3 = cmf0 != null && cmf0 > 0 && [1, 2, 3].some(off => {
    const prev = cmfAtOffset(highs, lows, closes, volumes, off);
    return prev != null && prev <= 0;
  });

  const preBreakout = !!(tightestSqueeze && nearRes3 && rvolTrendUp5 && cmfCrossUp3 && bb?.isSqueeze);
  if (preBreakout) {
    score += 3;
    breakdown.preBreakout += 3;
    labels.push("⚡ PRE-BREAKOUT COILING");
    signals.preBreakout = true;
    timeline.push({ phase: 2, text: "Phase 2: ✅ Pre-breakout coiling", ok: true });
  } else {
    signals.preBreakout = false;
    timeline.push({ phase: 2, text: "Phase 2: ⏳ Pre-breakout coiling not active", ok: false });
  }

  const of = optionsFlow;
  const optionsFlowBull = !!(of?.eligible !== false && of?.putCallOiRatio != null && of.putCallOiRatio < 0.72 && (of.otmCallOiShare ?? 0) >= 0.34);
  if (optionsFlowBull) {
    score += 2;
    breakdown.optionsFlowShift += 2;
    labels.push("📊 CALL FLOW INCREASING");
    signals.optionsFlow = true;
  } else {
    signals.optionsFlow = false;
  }

  const supportLvl = pivots?.s1 ?? (highs.length >= 15 ? Math.min(...lows.slice(0, 15)) : null);
  const nearSup3 = supportLvl ? [0, 1, 2].every(i => {
    const p = closes[i];
    return Math.abs(p - supportLvl) / supportLvl < 0.011;
  }) : false;
  const rvolDownDaysUp = (() => {
    let ok = true, cnt = 0;
    for (let i = 0; i < 5; i++) {
      if (closes[i] < closes[i + 1]) {
        const a = rvolAtOffset(volumes, i);
        const b = rvolAtOffset(volumes, i + 1);
        if (a != null && b != null && a > b) cnt++;
      }
    }
    return cnt >= 3;
  })();
  const cmfCrossDown3 = cmf0 != null && cmf0 < 0 && [1, 2, 3].some(off => {
    const prev = cmfAtOffset(highs, lows, closes, volumes, off);
    return prev != null && prev >= 0;
  });
  const preBreakdown = !!(tightestSqueeze && nearSup3 && rvolDownDaysUp && cmfCrossDown3 && bb?.isSqueeze && (price5d ?? 0) < 0);
  if (preBreakdown) {
    score -= 3;
    breakdown.preBreakdown -= 3;
    labels.push("⚡ PRE-BREAKDOWN COILING");
    signals.preBreakdown = true;
    timeline.push({ phase: 2, text: "Phase 2: ✅ Pre-breakdown coiling", ok: true });
  } else {
    signals.preBreakdown = false;
  }

  const spy3d = phaseContext?.spy3dPct;
  const stock3dFlat = price3d != null && Math.abs(price3d) < 0.85;
  const sectorOutperf = spy3d != null && sector3d != null && (sector3d - spy3d) > 1.5;
  const sectorLag = spy3d != null && sector3d != null && (spy3d - sector3d) > 1.5;
  const etfName = SECTOR_ETF_NAMES[phaseContext?.sectorEtf] || phaseContext?.sectorEtf || "Sector";
  if (sectorOutperf && stock3dFlat) {
    score += 2;
    breakdown.sectorRotation += 2;
    labels.push(`🏦 SECTOR LEADING — ${etfName} rotating in`);
    signals.sectorLeadingBull = true;
    signals.sectorLeadBull = true;
  } else if (sectorLag && stock3dFlat && price5d != null && price5d >= -0.5) {
    score -= 2;
    breakdown.sectorRotation -= 2;
    labels.push(`🏦 SECTOR LEADING — ${etfName} rotating out`);
    signals.sectorLeadingBear = true;
    signals.sectorLeadBear = true;
  } else {
    signals.sectorLeadingBull = false;
    signals.sectorLeadingBear = false;
  }

  score = Math.max(-9, Math.min(9, score));

  const phase1Any = accumulation || rsLeader || distribution;
  const phase2Any = preBreakout || optionsFlowBull || preBreakdown || signals.sectorLeadingBull || signals.sectorLeadingBear;

  const timelineLines = [
    ...timeline.map(t => t.text),
    `Phase 3: ⏳ Entry requires existing calls/puts confirmation (CMF, gate, etc.)`,
  ];

  return {
    phaseScore: score,
    phase1Bull: accumulation || rsLeader,
    phase1Bear: distribution,
    phase2Bull: preBreakout || optionsFlowBull || !!signals.sectorLeadingBull,
    phase2Bear: preBreakdown || !!signals.sectorLeadingBear,
    phase1Any,
    phase2Any,
    labels,
    timeline,
    timelineLines,
    signals,
    breakdown,
    sectorLeadBull: !!signals.sectorLeadingBull,
    sectorLeadBear: !!signals.sectorLeadingBear,
    progressionNote: null,
  };
}
function buildDeadCatPriceStory(symbol, dcb, earnDays) {
  if (!dcb || !symbol) return "";
  const sym = symbol.toUpperCase();
  const pct = dcb.declinePct != null ? Math.abs(dcb.declinePct).toFixed(1) : "—";
  const hi = dcb.high20 != null ? dcb.high20.toFixed(2) : "—";
  const nDays = dcb.daysFromHigh ?? 20;
  let text = `${sym} has fallen ${pct}% from its ${nDays}-day high of $${hi} over the last ${nDays} days.\n\n`;
  if (dcb.exhaustion_detected && dcb.climax_ratio != null) {
    text += `Volume spiked ${dcb.climax_ratio.toFixed(1)} times normal on a recent session, which can mean sellers are running out of steam.\n\n`;
  }
  if (dcb.bullish_divergence) {
    text += `Oversold momentum is making higher lows while price made a lower low — often an early sign a bounce may be building.\n\n`;
  }
  if (dcb.wick_rejection) {
    text += `The last few sessions show buyers pushing price up from the lows (long lower shadows).\n\n`;
  }
  if (dcb.bounce_trigger && dcb.rvol0 != null) {
    text += `A bounce-style session appeared recently with ${dcb.rvol0.toFixed(1)} times normal volume on an up day.\n\n`;
  }
  const hasSetup = dcb.phase1 || dcb.phase2 || dcb.phase3;
  if (hasSetup && dcb.bounceT1 != null && dcb.bounceT2 != null && dcb.bounceStop != null) {
    text += `SETUP: Possible bounce toward $${dcb.bounceT1.toFixed(2)} (Bounce T1, first recovery zone) or $${dcb.bounceT2.toFixed(2)} (Bounce T2, deeper recovery). A stop could sit near $${dcb.bounceStop.toFixed(2)}.\n\n`;
  } else {
    text += `No bounce-style setup is active right now.\n\n`;
  }
  if (typeof earnDays === "number" && earnDays >= 0 && earnDays <= 7) {
    text += `Earnings are in about ${earnDays} day${earnDays === 1 ? "" : "s"} — many traders wait until after the report for clarity.\n\n`;
  }
  if (dcb.warnings?.includes?.("cmf_penalty")) {
    text += `Money flow is still weak, so any bounce score was pulled back slightly.\n\n`;
  }
  if (dcb.warnings?.includes?.("rsi_phase1_block")) {
    text += `The dip is not deep enough on the main oversold reading to count the early bounce phase toward the score.\n\n`;
  }
  if (dcb.blocked && (dcb.warnings?.includes?.("earnings") || dcb.warnings?.includes?.("tide"))) {
    text += `Bounce-related score was not added — market or calendar conditions blocked it.\n\n`;
  }
  if (Array.isArray(dcb.labels) && dcb.labels.length > 0) {
    text += `${dcb.labels.join("\n")}\n\n`;
  }
  return text.trim();
}

/** Additive trade setups from raw.tradeSetups (api/scan.js + tradeSetups.js). Single winner by priority; bonus applied to buyScore. */
function applyTradeSetupsMerge(raw, ctx) {
  const {
    buyScore: bsIn, tideScore, cmf, rsi, earnDays, momentumBeforeDeadCat,
    deadCatBounce, pureCrypto, rawDcb, price, range52w,
  } = ctx;
  const t = raw?.tradeSetups;
  if (!t || pureCrypto) {
    return {
      buyScore: bsIn,
      activeSetupType: "MOMENTUM",
      setupBonus: 0,
      setupLabels: [],
      setupStory: "",
      tradeSetups: t || null,
    };
  }
  const dcb = rawDcb || raw?.deadCatBounce;
  const deadAny = !!(dcb && (dcb.phase1 || dcb.phase2 || dcb.phase3));

  const gap = t.gap || {};
  const ed = t.earningsDrift || {};
  const bf = t.bullFlag || {};
  const rb = t.rangeBreakout || {};
  const sb = t.supportBounce || {};
  const vcp = t.vcp || {};

  const earn = typeof earnDays === "number" ? earnDays : null;

  const gapGoBlock = rsi != null && rsi > 80
    || (cmf != null && cmf < 0)
    || (typeof tideScore === "number" && tideScore < -2)
    || (earn != null && earn >= 0 && earn <= 1);
  const gapFillBlock = (cmf != null && cmf > 0)
    || (rsi != null && rsi < 40)
    || (typeof tideScore === "number" && tideScore > 3);

  const edBlock = (cmf != null && cmf < 0)
    || (typeof tideScore === "number" && tideScore < -2)
    || ed.drift_failed;

  const bfBlockE = earn != null && earn >= 0 && earn <= 7;
  const bfBlockT = typeof tideScore === "number" && tideScore < -2;
  const bfBreakBlockCmf = bf.breakout_confirmed && cmf != null && cmf < 0;

  const rbBlockT = typeof tideScore === "number" && tideScore < -1;
  let rbPenalty = 0;
  if (earn != null && earn >= 0 && earn <= 14) rbPenalty = -2;

  const sbBlock = (typeof tideScore === "number" && tideScore < -3)
    || (cmf != null && cmf < -0.05)
    || (rsi != null && rsi < 35)
    || deadAny
    || !sb.in_uptrend;

  const candidates = [];

  if (vcp.active) {
    const vb = Math.min(10, typeof vcp.scoreProvisional === "number" ? vcp.scoreProvisional : 0);
    candidates.push({
      type: "VCP",
      priority: 0,
      bonus: vb,
      labels: vcp.breakout_confirmed ? ["💥 VCP BREAKOUT"] : ["🔄 VCP FORMING"],
    });
  }
  if (gap.gap_and_go && !gapGoBlock) {
    let b = 4;
    if (earn != null && earn >= 0 && earn <= 3) b += 2;
    candidates.push({ type: "GAP_AND_GO", priority: 1, bonus: b, labels: gap.labels || [] });
  }
  if (gap.gap_fill_setup && !gapFillBlock) {
    candidates.push({ type: "GAP_FILL", priority: 2, bonus: gap.scoreProvisional || -4, labels: gap.labels || [] });
  }
  if (ed.active && !ed.drift_failed && !edBlock) {
    candidates.push({ type: "EARNINGS_DRIFT", priority: 3, bonus: ed.scoreProvisional || 0, labels: ed.labels || [] });
  }
  if (bf.breakout_confirmed && !bfBlockE && !bfBlockT && !bfBreakBlockCmf) {
    candidates.push({ type: "BULL_FLAG", priority: 4, bonus: bf.scoreProvisional || 0, labels: bf.labels || [] });
  }
  if (rb.range_breakout_confirmed && !rbBlockT) {
    candidates.push({ type: "RANGE_BREAKOUT", priority: 5, bonus: (rb.scoreProvisional || 0) + rbPenalty, labels: rb.labels || [] });
  }
  if (sb.bounce_trigger && sb.active && !sbBlock) {
    candidates.push({ type: "SUPPORT_BOUNCE", priority: 6, bonus: sb.scoreProvisional || 0, labels: sb.labels || [] });
  }
  if (bf.valid_pole && bf.valid_flag && !bf.breakout_confirmed && !bfBlockE && !bfBlockT) {
    candidates.push({ type: "BULL_FLAG", priority: 7, bonus: Math.min(3, bf.scoreProvisional || 0), labels: bf.labels?.filter(l => l.includes("FORMING")) || [] });
  }
  if (rb.tight_range && rb.long_consolidation && !rb.range_breakout_confirmed && !rbBlockT) {
    const b = (rb.scoreProvisional || 0) + rbPenalty;
    if (b !== 0) candidates.push({ type: "RANGE_BREAKOUT", priority: 8, bonus: b, labels: rb.labels || [] });
  }
  if (sb.active && !sb.bounce_trigger && !sbBlock) {
    candidates.push({ type: "SUPPORT_BOUNCE", priority: 9, bonus: Math.min(4, sb.scoreProvisional || 0), labels: sb.labels || [] });
  }

  candidates.sort((a, b) => a.priority - b.priority);
  const win = candidates[0];
  let bonus = win ? win.bonus : 0;
  let activeSetupType = win ? win.type : "MOMENTUM";
  let setupLabels = win ? win.labels : [];

  if (momentumBeforeDeadCat >= 6 && ["BULL_FLAG", "RANGE_BREAKOUT", "SUPPORT_BOUNCE"].includes(activeSetupType)) {
    bonus = 0;
    activeSetupType = "MOMENTUM";
    setupLabels = [];
  }

  let buyScore = Math.round((bsIn + bonus) * 2) / 2;
  buyScore = Math.max(-15, Math.min(15, buyScore));

  const setupStory = buildTradeSetupWinnerStory(ctx.symbol || "", t, activeSetupType, { price, range52w, rsi, cmf, earnDays: earn });

  return {
    buyScore,
    activeSetupType,
    setupBonus: bonus,
    setupLabels,
    setupStory,
    tradeSetups: { ...t, _winner: win || null },
  };
}
const EARN_SOON_DAYS = 14;
const EARN_WINDOW_MIN = -5;
const EARN_WINDOW_MAX = 90;

function buildUnifiedPriceStory(symbol, deadCatStory, tradeSetup, unifiedDefault) {
  if (tradeSetup?.setupStory) return tradeSetup.setupStory;
  if (deadCatStory) return deadCatStory;
  return unifiedDefault || "";
}

function buildEarningsState(daysAway) {
  if (typeof daysAway !== "number" || daysAway < EARN_WINDOW_MIN || daysAway > EARN_WINDOW_MAX) {
    return { daysAway: null, date: null, soon: false, within7: false, warning: null, avoidOptions: false, source: null };
  }
  const soon = daysAway >= 0 && daysAway <= EARN_SOON_DAYS;
  const within7 = daysAway >= 0 && daysAway <= 7;
  return {
    daysAway,
    date: null,
    soon,
    within7,
    warning: soon ? `⚠ Earnings in ${daysAway} day${daysAway === 1 ? "" : "s"} — avoid options` : null,
    avoidOptions: soon,
    source: "scan",
  };
}

function runAnalysisFull(raw, ctx) {
  const spyReturn = ctx.spyReturn ?? null;
  const cryptoMode = !!ctx.cryptoMode;
  const symbol = ctx.symbol ?? raw?.symbol ?? null;
  const marketTide = ctx.marketTide ?? null;
  if (!raw || !raw.closes || raw.closes.length < 5) return null;
  const { closes, highs, lows, volumes, optionsFlow, phaseContext } = raw;
  const sym = String(ctx.symbol ?? raw.symbol ?? "").toUpperCase();
  const earningsState = {
    ...buildEarningsState(raw.earningsDaysAway),
    nextDateIso: raw.earningsNextDate ?? raw.earningsState?.nextDateIso ?? null,
    source: raw.earningsSource ?? raw.earningsState?.source ?? null,
  };
  const price = closes[0];
  const useSmaTrend = ctx.maTrendLines === "sma";
  const sm50 = useSmaTrend ? smaLatest(closes, 50) : emaLatest(closes, 50);
  const sm200 = useSmaTrend ? smaLatest(closes, 200) : emaLatest(closes, 200);
  const ema21 = emaArr(closes, 21)[0];
  const ema9  = emaArr(closes, 9)[0];
  const ema9Arr  = emaArr(closes, 9);
  const ema21Arr = emaArr(closes, 21);
  const ema21Prev5 = ema21Arr.length > 5 && ema21Arr[5] != null && ema21Arr[5] > 0 ? ema21Arr[5] : null;
  const ema21SlopePct5 = ema21 != null && ema21Prev5 != null ? ((ema21 - ema21Prev5) / ema21Prev5) * 100 : null;
  const ema21Slope = ema21SlopePct5 == null ? null : (ema21SlopePct5 > 0.1 ? "rising" : ema21SlopePct5 < -0.1 ? "falling" : "flat");
  const ema21SlopeLabel = ema21Slope === "rising" ? "EMA21 Rising" : ema21Slope === "falling" ? "EMA21 Falling" : ema21Slope === "flat" ? "EMA21 Flat" : null;
  const ema21PositionScore = (() => {
    if (ema21 == null) return 0;
    if (ema21SlopePct5 == null) return price > ema21 ? 2 : -2;
    const rising = ema21SlopePct5 > 0.1;
    const falling = ema21SlopePct5 < -0.1;
    const flat = !rising && !falling;
    if (price > ema21) {
      if (rising) return 2;
      if (flat) return 1;
      return 0;
    }
    if (falling) return -2;
    if (flat) return -1;
    return -1;
  })();
  // 9/21 EMA cross — professional options entry trigger
  const ema9AboveEma21     = ema9 != null && ema21 != null && ema9 > ema21;
  const pricePulledToEma9  = ema9 != null && closes[0] != null && Math.abs(closes[0] - ema9) / ema9 < 0.015; // within 1.5% of EMA9
  const nineEmaEntry       = ema9AboveEma21 && pricePulledToEma9; // textbook calls entry trigger
  const rsi = calcRSI(closes);
  const macd = calcMACD(closes);
  const bb = calcBB(closes);
  const cmf = calcCMF(highs, lows, closes, volumes);
  const cmfDiv = calcCMFDiv(highs, lows, closes, volumes);
  const vwap = calcVWAP(highs, lows, closes, volumes);
  const pctChange = ((closes[0] - closes[1]) / closes[1]) * 100;
  const stock10Return = closes[10] ? ((closes[0] - closes[10]) / closes[10]) * 100 : null;
  const rsVsSpy10 = raw.relativeStrength?.vs_spy_10d ?? ((stock10Return != null && marketTide?.spy10Return != null) ? (stock10Return - marketTide.spy10Return) : null);
  const consolidation = raw.consolidation ?? null;
  const ema8 = raw.ema8 ?? null;
  const ema20 = raw.ema20 ?? null;
  const emaAlignment = raw.emaAlignment ?? null;
  const relativeStrength = raw.relativeStrength ?? null;
  const downVsUpVol = (() => {
    if (!Array.isArray(volumes) || volumes.length < 12 || closes.length < 12) return null;
    let down = 0, up = 0;
    for (let i = 0; i < 10; i++) {
      const c0 = closes[i], c1 = closes[i + 1];
      const v0 = volumes[i] || 0;
      if (c0 < c1) down += v0;
      else if (c0 > c1) up += v0;
    }
    return { down, up, downGtUp: down > up };
  })();
  const goldenCross = sm50 != null && sm200 != null && sm50 > sm200;
  const deathCross = sm50 != null && sm200 != null && sm50 < sm200;
  const sma200StructureScore = (() => {
    if (goldenCross) return 1;
    if (sm200 == null || sm200 <= 0) return -1;
    const pctFrom200 = ((price - sm200) / sm200) * 100;
    if (price < sm200) {
      const pctBelow = (sm200 - price) / sm200 * 100;
      if (deathCross && pctBelow > 10) return -3;
      if (pctBelow > 10) return -2;
      return 0;
    }
    if (pctFrom200 > 15) return -1;
    return 1;
  })();
  const atr = calcATR(highs, lows, closes);
  const volDiv = calcVolDiv(closes, volumes);
  const fibs = calcFibs(closes, 63, highs, lows);
  const pivots = calcPivots(highs, lows, closes);
  const priorBuyScore = raw?.priorBuyScore ?? raw?._priorBuyScore ?? null;
  let rsiDiv = calcRSIDiv(closes, highs, lows, {
    volumes,
    cmf,
    price,
    priorBuyScore: priorBuyScore != null ? Number(priorBuyScore) : null,
    currentBuyScore: null,
  });
  const range52w = calc52w(closes);
  const threeDayRule = calc3Day(closes);
  const adx = calcADX(highs, lows, closes);
  const stoch = calcStoch(highs, lows, closes);
  const obv = calcOBV(closes, volumes);
  const psar = calcParabolicSAR(highs, lows, closes);
  const rvol = calcRVOL(volumes);
  const trendStructure = calcTrendStructure(highs, lows, closes);
  const fibRetracement = computeFibRetracementScore({
    highs,
    lows,
    closes,
    volumes,
    price,
    cmf,
    uptrendIntact: goldenCross === true || trendStructure?.bullish === true,
    todayLow: lows[0],
    todayClose: closes[0],
  });
  const williamsR = calcWilliamsR(highs, lows, closes);
  const volBreakout = calcVolumeBreakout(closes, highs, lows, volumes, bb, ema21);
  // Volume Climax — context-aware: breakout confirmation vs exhaustion vs neutral
  const volClimax = (() => {
    if (!rvol || !closes.length) return null;
    const ratio = rvol.ratio;
    const newHigh = highs.length >= 20 && closes[0] >= Math.max(...highs.slice(1, 20));
    const rsiVal = rsi;
    let score = 0;
    let uiLabel = "Neutral";
    let kind = "neutral";
    const blockCalls = false;
    if (ratio > 2 && rsiVal != null && rsiVal >= 45 && newHigh) {
      score = 2;
      uiLabel = "Breakout Vol";
      kind = "breakout";
    } else if (ratio > 1.5 && rsiVal != null && rsiVal >= 45) {
      score = 1;
      uiLabel = "Breakout Vol";
      kind = "breakout";
    }
    return {
      score,
      uiLabel,
      kind,
      blockCalls,
      ratio,
      newHigh,
      pctChange,
    };
  })();
  // WR_RECOVERY — 5-factor mean reversion composite signal
  // Only fires when ALL conditions align — not just Williams %R alone
  const wrRecovery = (() => {
    if (!williamsR?.bullCross) return null; // must have the transition
    const rsiRecov  = rsi != null && rsi < 40 && rsi > 20; // recovering not crashing
    const cmfSafe   = cmf != null && cmf > -0.05; // not actively distributing
    const nearEMA   = ema21 && price >= ema21 * 0.98; // at or within 2% of EMA21
    const noDCross  = !(sm50 && sm200 && sm50 < sm200); // no death cross
    const allPass   = rsiRecov && cmfSafe && nearEMA && noDCross;
    if (!allPass) return null;
    const reasons = [
      `Williams %R crossed above -80 (${williamsR.value}) — exiting oversold`,
      `RSI ${rsi?.toFixed(0)} recovering — momentum turning up`,
      `CMF ${cmf?.toFixed(3)} — no institutional distribution`,
      nearEMA && price < ema21 ? `Price within 2% of EMA21 ($${ema21?.toFixed(2)})` : `Price above EMA21 ($${ema21?.toFixed(2)})`,
    ];
    return { active: true, reasons };
  })();
  const recentHigh = Math.max(...closes.slice(0, 252));
  const pureCrypto = isPureCrypto(symbol);

  // Crypto-adjusted thresholds — defined before signalChecks so they apply consistently
  const rsiOverbought = cryptoMode ? 75 : 70;
  const rsiOversold   = cryptoMode ? 35 : 30;
  const rsiExtended   = cryptoMode ? 72 : 65;
  const rsiHot        = cryptoMode ? 65 : 60;
  const rsiSweet      = cryptoMode ? 50 : 45;

  // Weekly trend (resampled from daily — no extra API call)
  const weeklyTrend = calcWeeklyTrend(closes);

  // Relative Strength — vs SPY for equities, vs BTC for pure crypto alts
  const stockReturn63 = closes[63] ? ((closes[0] - closes[63]) / closes[63]) * 100 : null;
  const stockReturn20 = closes[20] ? ((closes[0] - closes[20]) / closes[20]) * 100 : null;
  const spyReturn20 = phaseContext?.spy20dPct ?? null;
  const spyReturn63 = phaseContext?.spy63dPct ?? (spyReturn != null ? spyReturn : null);
  // For pure crypto: compare to BTC 24h trend as a proxy (module-level ctx.btcData)
  const btcBullish = ctx.btcData?.bullish ?? null;
  const rs = pureCrypto
    ? (stockReturn63 != null ? {
        stockReturn: stockReturn63,
        spyReturn: null,
        outperforming: btcBullish !== null ? (btcBullish ? stockReturn63 > 0 : stockReturn63 < 0) : null,
        spread: null,
        label: btcBullish === null ? "NO BTC DATA" : (stockReturn63 > 5 ? "STRONG" : stockReturn63 > 0 ? "POSITIVE" : stockReturn63 > -5 ? "WEAK" : "LAGGING"),
        vsBTC: true,
      } : null)
    : (stockReturn63 != null && spyReturn != null ? {
        stockReturn: stockReturn63,
        spyReturn,
        outperforming: stockReturn63 > spyReturn,
        spread: stockReturn63 - spyReturn,
        label: stockReturn63 > spyReturn + 5 ? "STRONG" : stockReturn63 > spyReturn ? "POSITIVE" : stockReturn63 > spyReturn - 5 ? "WEAK" : "LAGGING",
      } : stockReturn63 != null ? {
        stockReturn: stockReturn63,
        spyReturn: null,
        outperforming: null,
        spread: null,
        label: "NO SPY DATA",
      } : null);

  // Fear & Greed as a crypto-specific signal (module-level ctx.fearGreedData)
  const fgScore = ctx.fearGreedData?.score ?? null;
  const cryptoFGBull = pureCrypto && fgScore !== null && fgScore >= 55; // Greed zone = momentum buying
  const cryptoFGBear = pureCrypto && fgScore !== null && fgScore <= 25; // Extreme Fear = oversold opportunity

  // Individual signal breakdown — count how many of 22 are bullish
  // Note: RSI thresholds now use crypto-adjusted vars defined above
  const rsLabel = pureCrypto ? "RS vs BTC" : "RS vs SPY";

  // ── KEY LEVEL PROXIMITY (must be before signalChecks — used in display + buyScore)
  // ATR(14)-adaptive bands — replaces static $/% proximity thresholds

  // Sector relative strength — must be before buyScore which uses it
  const sectorEtf = symbol ? ctx.tickerSectorMap[symbol] : null;
  const sectorData = sectorEtf ? ctx.sectorETFData[sectorEtf] : null;
  const sectorReturn = sectorData?.prevClose ? ((sectorData.price - sectorData.prevClose) / sectorData.prevClose) * 100 : null;
  const rsVsSector = sectorReturn != null && stockReturn63 != null ? {
    etf: sectorEtf,
    sectorReturn,
    stockReturn: stockReturn63,
    outperforming: stockReturn63 > sectorReturn,
    spread: stockReturn63 - sectorReturn,
    label: stockReturn63 > sectorReturn + 5 ? "LEADING SECTOR" : stockReturn63 > sectorReturn ? "ABOVE SECTOR" : stockReturn63 > sectorReturn - 5 ? "IN LINE" : "LAGGING SECTOR",
  } : null;
  const keyLevelProximity = (() => {
    if (!atr || atr <= 0 || price == null) {
      return { band: null, extendedThresh: null, score: 0, reason: "none", trendingUp: false, nearestBelow: null, atr14: atr ?? null };
    }
    const band = 0.5 * atr;
    const extendedThresh = 1.5 * atr;
    const trendingUp = goldenCross === true || trendStructure?.bullish === true;
    const fibPivotSupports = [fibs?.fib382, fibs?.fib500, fibs?.fib618, pivots?.s1, pivots?.s2].filter(L => L != null && L > 0);
    const resistances = [pivots?.r1, pivots?.r2, fibs?.fib236].filter(L => L != null && L > 0);
    const nearFibPivot = fibPivotSupports.some(L => Math.abs(price - L) <= band);
    const nearEma21Support = ema21 != null && price >= ema21 && Math.abs(price - ema21) <= band;
    const nearSupport = nearFibPivot || nearEma21Support;
    const nearResistance = resistances.some(L => Math.abs(price - L) <= band);
    const supportsBelow = [
      ...fibPivotSupports.filter(L => L < price),
      ...(ema21 != null && ema21 < price ? [ema21] : []),
    ];
    const nearestBelow = supportsBelow.length ? Math.max(...supportsBelow) : null;
    const extendedFromSupport = nearestBelow != null && (price - nearestBelow) > extendedThresh;
    let score = 0;
    let reason = "neutral";
    if (nearSupport) {
      score = 1.5;
      reason = "support";
    } else if (nearResistance && trendingUp) {
      score = 1;
      reason = "resistance";
    } else if (extendedFromSupport) {
      score = -1;
      reason = "no_mans_land";
    }
    return { band, extendedThresh, score, reason, trendingUp, nearestBelow, atr14: atr };
  })();
  const keyLevelBonus = keyLevelProximity.score;

  const signalChecks = [
    { label: "200 SMA Structure", tooltip: "Golden cross (SMA50 > SMA200) = +1. Otherwise score uses price vs 200 SMA: >15% above = extended (−1); 0–15% above = healthy (+1); 0–10% below = neutral (0); >10% below = broken (−2); bearish 50/200 cross + >10% below = full breakdown (−3).", bull: goldenCross, bear: deathCross },
    { label: ema21SlopeLabel || "EMA21", tooltip: `Price vs 21-day EMA with 5-bar slope (${ema21SlopePct5 != null ? `${ema21SlopePct5 >= 0 ? "+" : ""}${ema21SlopePct5.toFixed(2)}%` : "n/a"} vs 5 bars ago). Score: above + rising +2; above + flat +1; above + falling 0; below + falling −2; below + flat −1; below + rising −1.`, bull: price > ema21, bear: price < ema21 },
    { label: "RSI", tooltip: `Relative Strength Index (0–100). Midline rules: cross above 50 +2, above 50 rising +1, below 50 falling −1, cross below 50 −2. RSI > 70 = strong momentum (no penalty). Below ${rsiOversold} = mild bounce +1.`,
                                 bull: rsi != null && rsi < rsiSweet && rsi > rsiOversold,  bear: false },
    { label: "RSI Divergence", tooltip: rsiDiv?.bearish ? "⚠ Bearish divergence — price higher high, RSI lower high (last 20 bars). Score −2." : rsiDiv?.bullish ? "✅ Bullish divergence — price lower low, RSI higher low. Score +2." : "No 20-bar RSI/price divergence.", bull: rsiDiv?.bullish === true, bear: rsiDiv?.bearish === true },
    { label: "MACD (display only)", tooltip: "MACD is shown for context only — not used in score, gate, verdict, or entry signals (v2).", bull: false, bear: false },
    { label: "CMF", tooltip: "Chaikin Money Flow (-1 to +1). Above +0.05 = institutions accumulating. Below -0.05 = institutions distributing. Above +0.15 = strong institutional buying — not just retail.",              bull: cmf != null && cmf > 0.05,                bear: cmf != null && cmf < -0.05 },
    { label: "CMF Divergence", tooltip: cmfDiv ? cmfDiv.tooltip : "No CMF divergence detected.", bull: cmfDiv?.bullishDiv === true, bear: cmfDiv?.bearishDiv === true, },
    { label: "VWAP", tooltip: "Volume Weighted Average Price — the fair value for the day. Price above VWAP = buyers in control. Institutions use VWAP as a benchmark. Crossing it is significant.",             bull: vwap != null && price > vwap,             bear: vwap != null && price < vwap },
    { label: "Volume",           bull: volDiv?.bullish === true,                  bear: volDiv?.bearish === true },
    { label: "52W Range", tooltip: "Where price sits in its 52-week high-to-low range. Near low (10–35%) = beaten down, more room to run. Near high (65–90%) = extended, less upside.",
                                 bull: range52w?.nearLow === true,               bear: range52w?.nearHigh === true && rsi > rsiHot },
    { label: "ADX Trend",        bull: adx?.strong && goldenCross,               bear: adx?.ranging === true },
    { label: "Stochastic",       bull: stoch?.oversold || stoch?.bullCross,       bear: stoch?.overbought || stoch?.bearCross },
    { label: "OBV", tooltip: "On-Balance Volume. CONFIRMING = OBV rising with price (smart money buying). DIVERGING = OBV falling while price rises (price may be faking higher without real buyers — warning sign).",              bull: obv?.bullishDiv || (obv?.confirming && goldenCross), bear: obv?.bearishDiv },
    { label: "Parabolic SAR", tooltip: "Parabolic Stop and Reverse. Dots below price = BULLISH uptrend. Dots above price = BEARISH downtrend. When SAR flips from above to below price, it signals a potential trend reversal up.",    bull: psar?.bullish === true,                   bear: psar?.bullish === false },
    { label: "RVOL", tooltip: "Relative Volume — today's volume vs. the daily average. 1.5x = 50% above average. 2x+ = unusually high. Moves on high RVOL are more likely to sustain than moves on thin volume.",             bull: rvol?.surge && goldenCross,               bear: rvol?.low === true },
    { label: "Trend Structure", tooltip: "Analyzes highs and lows to determine trend. UPTREND (HH+HL) = Higher Highs + Higher Lows — the classic uptrend. DOWNTREND (LL+LH) = Lower Lows + Lower Highs. Rangebound = neither.",  bull: trendStructure?.bullish === true,          bear: trendStructure?.bearish === true },
    { label: "BB Squeeze",       bull: bb?.isSqueeze === true,                   bear: false },
    { label: "Key Level", tooltip: keyLevelProximity.band != null
      ? `ATR(14) adaptive proximity. Band = 0.5×ATR = $${keyLevelProximity.band.toFixed(2)} (price within ±$${keyLevelProximity.band.toFixed(2)} of a level). Support zone (Fib 38.2/50/61.8, Pivot S1/S2, EMA21) = +1.5. Resistance test (Pivot R1/R2, Fib 23.6) + uptrend = +1. Extended >1.5×ATR ($${keyLevelProximity.extendedThresh.toFixed(2)}) above nearest support below = −1.`
      : "ATR unavailable — key level proximity score 0.",
      bull: keyLevelBonus > 0,   bear: keyLevelBonus < 0 },
    { label: rsLabel, tooltip: pureCrypto ? "3-month return vs BTC trend. Positive = crypto is holding or gaining vs broader crypto market." : "3-month return vs SPY. Outperforming = stock has relative strength vs the market.",
                                 bull: rs?.outperforming === true,               bear: rs?.outperforming === false },
  { label: "Weekly Trend", tooltip: "Weekly chart (resampled from dailies): EMA8/21 structure shown as STRONG UP / WEAK UP / DOWN. Score multiplier (applied after sum): price above weekly EMA21 + weekly MACD bullish = ×1; neutral = ×1; price below weekly EMA21 + weekly MACD bearish with positive raw score = ×0.5 (counter-trend warning).",
                                 bull: weeklyTrend?.weeklyBullish === true,      bear: weeklyTrend?.weeklyBearish === true },
  { label: "Williams %R",   tooltip: "Williams %R (-100 to 0). Below -80 = oversold. Above -20 = overbought. The key signal is the TRANSITION — %R crossing back above -80 from deeply oversold is a mean-reversion buy signal. %R crossing below -20 from overbought is a distribution warning.", bull: williamsR?.bullCross === true, bear: williamsR?.bearCross === true || williamsR?.overbought === true },
  { label: "Vol Breakout",  tooltip: "Validates whether a breakout has volume support. CONFIRMED = breakout with 1.5x+ average volume. FAKEOUT RISK = breakout on low volume — high failure rate. VOL SURGE = 2x+ volume without a price breakout — potential accumulation or news catalyst.", bull: volBreakout?.volConfirmed === true, bear: volBreakout?.volFakeout === true },
  { label: "Sector RS", tooltip: "How this stock is performing vs its own sector ETF over 3 months. LEADING = outperforming its sector peers — stock-specific strength, not just macro tide. LAGGING = underperforming its sector — stock-specific weakness even when the sector is healthy.", bull: rsVsSector?.label === "LEADING SECTOR", bear: rsVsSector?.label === "LAGGING SECTOR" },
  { label: "Vol Climax", tooltip: "Context-aware volume: BREAKOUT — vol >2× avg + RSI 45–70 + new 20D high = +2; healthy accumulation = vol >1.5× + RSI 45–65 = +1. CLIMAX — vol >3× + RSI >75 = −2 (blocks calls); vol >2× + RSI >70 + day >+5% = −1. All other cases = 0 (neutral).", bull: volClimax != null && volClimax.score > 0, bear: volClimax != null && volClimax.score < 0 },
  ];
  // Crypto Fear & Greed as a 17th signal for pure crypto (replaces no signal — additive)
  if (pureCrypto && fgScore !== null) {
    signalChecks.push({ label: "Fear & Greed", tooltip: `CNN Fear & Greed Index: ${fgScore}/100 (${ctx.fearGreedData?.rating}). Below 25 = Extreme Fear (oversold opportunity). Above 75 = Extreme Greed (extended, risk of reversal).`,
      bull: cryptoFGBear, // Extreme Fear = buy signal (contrarian)
      bear: fgScore >= 80, // Extreme Greed = caution
    });
  }
  const bullCount = signalChecks.filter(c => c.bull).length;
  const bearCount = signalChecks.filter(c => c.bear).length;

  // ── PRE-SCORE GATES (Calls/Puts) — computed BEFORE buyScoreBeforeWeekly ───
  const tideScoreForGate = marketTide?.breadthScore;
  const deadCatException = !!(raw.deadCatBounce?.eligible === true && (
    raw.deadCatBounce.phase1 || raw.deadCatBounce.phase2 || raw.deadCatBounce.phase3
    || raw.deadCatBounce.bounce_trigger
  ));
  const gapFillException = !!(raw.tradeSetups?.gap?.gap_fill_setup);

  // SMA200 trend (robust): compare now vs 20 bars ago AND require 20 consecutive bars in same direction.
  // Only count as trending down if SMA200 is ≥0.5% lower than 20 bars ago (noise filter).
  const sma200Trend = (() => {
    if (!closes || closes.length < 221) return { up: null, down: null, sma200Now: sm200 ?? null, sma200Ago20: null };
    const arr = [];
    for (let off = 0; off <= 20; off++) {
      const v = sma(closes.slice(off), 200);
      if (v == null || !Number.isFinite(v)) return { up: null, down: null, sma200Now: sm200 ?? null, sma200Ago20: null };
      arr.push(v);
    }
    // closes[0]=today. For a sustained downtrend: sma200(today) < sma200(1d ago) < ... < sma200(20d ago)
    const consecutiveDown = arr.slice(0, 20).every((v, i) => v < arr[i + 1]);
    const consecutiveUp = arr.slice(0, 20).every((v, i) => v > arr[i + 1]);
    const now = arr[0];
    const ago20 = arr[20];
    const pctChange = ago20 > 0 ? ((now - ago20) / ago20) * 100 : 0;
    const down = consecutiveDown && pctChange <= -0.5;
    const up = consecutiveUp && pctChange >= 0.5;
    return { up, down, sma200Now: now, sma200Ago20: ago20 };
  })();
  const sma200TrendingUp = sma200Trend.up;
  const sma200TrendingDown = sma200Trend.down;

  const week52Low = range52w?.low ?? null;
  const week52High = range52w?.high ?? null;

  const obvSeries = (() => {
    if (!closes?.length || !volumes?.length) return null;
    let v = 0;
    const arr = [0];
    for (let i = 0; i < closes.length - 1; i++) {
      if (closes[i] > closes[i + 1]) v += volumes[i] || 0;
      else if (closes[i] < closes[i + 1]) v -= volumes[i] || 0;
      arr.push(v);
    }
    return arr;
  })();
  const obv0 = obvSeries?.[0] ?? null;
  const obv6 = obvSeries?.[6] ?? null;

  const gates = (() => {
    const mkGate = (pass, label, detail) => ({ pass: !!pass, label, detail: detail || "" });
    const primary = (list) => {
      const f = list.find(g => !g.pass);
      return f ? f.label : null;
    };

    // CALLS
    const c1Pass = (typeof tideScoreForGate !== "number" ? true : tideScoreForGate >= 0) || deadCatException;
    const c1 = mkGate(c1Pass, "🌊 Market Tide supports calls", deadCatException && (typeof tideScoreForGate === "number" && tideScoreForGate < 0) ? "Dead cat bounce exception" : "");

    const c2Fails = [];
    if (sm200 != null && !(price > sm200)) c2Fails.push("Price below 200 SMA");
    if (sma200TrendingUp === false) c2Fails.push("200 SMA trending down");
    if (ema21Slope != null && ema21Slope !== "rising") c2Fails.push("EMA21 slope not rising");
    if (sm50 != null && sm200 != null && !(sm50 > sm200)) c2Fails.push("50 SMA below 200 SMA");
    const c2Pass = c2Fails.length === 0;
    const c2 = mkGate(c2Pass, "Stage 2 trend", c2Fails[0] || "");

    const c3Fails = [];
    if (week52Low != null && !(price >= week52Low * 1.25)) c3Fails.push("Price too close to 52W low");
    if (week52High != null && !(price >= week52High * 0.75)) c3Fails.push("Price too far below 52W high (need to be within 25%)");
    if (sma200TrendingUp === false) c3Fails.push("200 SMA trending down");
    const c3Pass = c3Fails.length === 0;
    const c3 = mkGate(c3Pass, "Trend template", c3Fails[0] || "");

    const c4Fails = [];
    if (cmf != null && !(cmf > -0.02)) c4Fails.push("CMF negative");
    if (obv0 != null && obv6 != null && !(obv0 >= obv6 * 0.98)) c4Fails.push("OBV declining");
    const c4Pass = c4Fails.length === 0;
    const c4 = mkGate(c4Pass, "Institutional support", c4Fails[0] || "");

    // PUTS
    const p1Pass = (typeof tideScoreForGate !== "number" ? true : tideScoreForGate <= 0) || gapFillException;
    const p1 = mkGate(p1Pass, "🌊 Market Tide supports puts", gapFillException && (typeof tideScoreForGate === "number" && tideScoreForGate > 0) ? "Gap fill exception" : "");

    const p2Fails = [];
    if (sm200 != null && !(price < sm200)) p2Fails.push("Price above 200 SMA");
    if (sma200TrendingDown === false) p2Fails.push("200 SMA trending up");
    if (ema21Slope != null && ema21Slope !== "falling") p2Fails.push("EMA21 slope not falling");
    if (sm50 != null && sm200 != null && !(sm50 < sm200)) p2Fails.push("50 SMA above 200 SMA");
    const p2Pass = p2Fails.length === 0;
    const p2 = mkGate(p2Pass, "Stage 4 trend", p2Fails[0] || "");

    const p3Fails = [];
    if (week52High != null && !(price <= week52High * 0.80)) p3Fails.push("Price too close to 52W high (need to be 20%+ below)");
    if (week52Low != null && !(price <= week52Low * 1.30)) p3Fails.push("Price too far above 52W low (must be within 30%)");
    if (sma200TrendingDown === false) p3Fails.push("200 SMA trending up");
    const p3Pass = p3Fails.length === 0;
    const p3 = mkGate(p3Pass, "Breakdown template", p3Fails[0] || "");

    const p4Fails = [];
    if (cmf != null && !(cmf < 0.02)) p4Fails.push("CMF positive");
    if (obv0 != null && obv6 != null && !(obv0 <= obv6 * 1.02)) p4Fails.push("OBV rising");
    const p4Pass = p4Fails.length === 0;
    const p4 = mkGate(p4Pass, "Institutional distribution", p4Fails[0] || "");

    // Context-only — not enforced on verdict/score. Hard blocks live in callsSignal/putsSignal + calcGate.
    // TODO(Phase 1): sector tide gate — add gate5 using phaseContext.sector3dPct vs spy3dPct
    // TODO(Phase 2): earnings hard gate — wire earningsState.daysAway into callsContext/putsContext

    return {
      callsContext: {
        gate1: c1,
        gate2: c2,
        gate3: c3,
        gate4: c4,
        primaryFailLabel: primary([c1, c2, c3, c4]),
      },
      putsContext: {
        gate1: p1,
        gate2: p2,
        gate3: p3,
        gate4: p4,
        primaryFailLabel: primary([p1, p2, p3, p4]),
      },
    };
  })();

  // ── SCORING v2 core (MACD display-only; not in score) ───────────────────────
  const scoringV2Core = computeCoreBuyScoreV2({
    price,
    closes,
    highs,
    lows,
    volumes,
    sm50,
    sm200,
    cmf,
    bb,
    rvol,
    phaseContext,
    stockReturn20,
    stockReturn63,
    spyReturn20,
    spyReturn63,
    pureCrypto,
  });
  const rsiCore = calcRsiMidlineScore(closes);
  const fibScoreComponent = fibRetracement.fibScore ?? 0;
  const rsiDivScore = (rsiDiv?.bullish ? 2 : rsiDiv?.bearish ? -2 : 0);
  const breakout52wScore = (range52w ? calc52wBreakoutSignalScore(range52w, rvol) : 0);
  const obvDivScore = (obv ? (obv.bullishDiv ? 2 : obv.bearishDiv ? -2 : 0) : 0);
  const addonBeforeWeekly =
    rsiDivScore + keyLevelBonus + breakout52wScore + obvDivScore + fibScoreComponent;
  const buyScoreBeforeWeekly = Math.round((scoringV2Core.buyScoreCore + addonBeforeWeekly) * 2) / 2;
  const { trend: trendCore, rsCore: rsSpyCore, money: cmfCore, momentum: momentumCore, breakout: breakoutCore, squeeze: bbCore, sector: sectorRotationCore } =
    scoringV2Core.breakdown;
  const macdCore = 0;

  const breadthScoreForWeeklyOverride = marketTide?.breadthScore;
  const weeklyBearishApplies = weeklyTrend?.weeklyBearish === true && buyScoreBeforeWeekly > 0;
  let weeklyTrendMultiplier = 1;
  let weeklyBearishTideOverrideLabel = null;
  if (weeklyBearishApplies) {
    if (typeof breadthScoreForWeeklyOverride === "number" && breadthScoreForWeeklyOverride >= 4) {
      weeklyTrendMultiplier = 1;
      weeklyBearishTideOverrideLabel = "Weekly Bearish Overridden by Bull Tide";
    } else if (typeof breadthScoreForWeeklyOverride === "number" && breadthScoreForWeeklyOverride >= 3) {
      weeklyTrendMultiplier = 0.75;
    } else {
      weeklyTrendMultiplier = 0.5;
    }
  }

  const counterTrendWeeklyCut = weeklyTrendMultiplier < 1;

  const earlyWarning = computeEarlyWarningPhases({
    closes, highs, lows, volumes, bb, pivots, range52w, cmf, obv, rvol, rs, rsVsSector,
    symbol, optionsFlow, phaseContext, pureCrypto, price, stockReturn63,
  });
  let addonRunning = addonBeforeWeekly + (earlyWarning.phaseScore ?? 0);
  let buyScore = applyAddonCap(scoringV2Core.buyScoreCore, addonRunning);
  buyScore = Math.round(buyScore * weeklyTrendMultiplier * 2) / 2;
  const buyScoreBeforeTide = buyScore;

  // ── MARKET TIDE MULTIPLIER (post-score) ───────────────────────────────────
  // Apply AFTER the 22-indicator score is computed (and after weekly multiplier).
  const tideScore = marketTide?.breadthScore;
  let tideMultiplier = 1;
  let tideBand = "NEUTRAL";
  if (typeof tideScore === "number") {
    tideBand = tideScore >= 4 ? "STRONG_BULL"
      : tideScore >= 2 ? "BULL"
      : tideScore <= -4 ? "STRONG_BEAR"
      : tideScore <= -2 ? "BEAR"
      : "NEUTRAL";
    if (tideBand === "STRONG_BULL") {
      tideMultiplier = buyScore > 0 ? 1.25 : buyScore < 0 ? 0.5 : 1;
    } else if (tideBand === "BULL") {
      tideMultiplier = buyScore > 0 ? 1.1 : buyScore < 0 ? 0.75 : 1;
    } else if (tideBand === "BEAR") {
      tideMultiplier = buyScore < 0 ? 1.1 : buyScore > 0 ? 0.75 : 1;
    } else if (tideBand === "STRONG_BEAR") {
      tideMultiplier = buyScore < 0 ? 1.25 : buyScore > 0 ? 0.5 : 1;
    }
  }
  if (tideMultiplier !== 1 && buyScore !== 0) {
    buyScore = Math.round(buyScore * tideMultiplier * 2) / 2;
  }
  buyScore = clampScore(buyScore);
  const buyScoreAfterTideMultipliers = buyScore;

  const tradeLevelsV2 = computeTradeLevelsV2({
    price,
    highs,
    lows,
    closes,
    sm50,
    atr,
    range52w,
  });
  let { stopPrice, t1, t2, rr1, rr2, rr1BelowTarget, target_basis } = tradeLevelsV2;

  const catchingKnivesCallsBlock = vwap != null && ema21 != null && price < vwap && price < ema21;
  const sellFlags = {
    macdCrossDown: false,
    belowEma21: price < ema21,
    aboveBBUpper: bb ? price > bb.upper : false,
    cmfNegative: cmf != null && cmf < -0.05,
  };
  const sellCount = Object.values(sellFlags).filter(Boolean).length;

  const t1Source = rr1BelowTarget ? "52W high (R:R < 1.5)" : "Structure v2";
  const t2Source = "Structure v2";

  // ── DOWNSIDE TARGETS — for bearish setups ────────────────────────────────
  // Priority: Pivot S1/S2 → Fib 38.2/61.8% retracement → ATR projection
  const d1 = (() => {
    const candidates = [];
    if (pivots?.s1 && pivots.s1 < price * 0.995) candidates.push(pivots.s1);
    if (fibs?.fib382 && fibs.fib382 < price * 0.995) candidates.push(fibs.fib382);
    if (bb?.lower && bb.lower < price * 0.995) candidates.push(bb.lower);
    if (atr) candidates.push(price - 1.5 * atr);
    const valid = candidates.filter(c => c < price * 0.995).sort((a, b) => b - a);
    return valid[0] ?? price * 0.92;
  })();

  const d2 = (() => {
    const candidates = [];
    if (pivots?.s2 && pivots.s2 < d1 * 0.995) candidates.push(pivots.s2);
    if (fibs?.fib618 && fibs.fib618 < d1 * 0.995) candidates.push(fibs.fib618);
    if (atr) candidates.push(price - 3 * atr);
    const valid = candidates.filter(c => c < d1 * 0.995).sort((a, b) => b - a);
    return valid[0] ?? d1 * 0.95;
  })();

  // Entry gate + options signals (two passes so Phase 1→2→3 +1 can affect score thresholds and gate)
  const atrStop2x = stopPrice ?? (atr ? price - 2 * atr : null);
  let gate;
  let callsSignal;
  let putsSignal;
  let bs = buyScore;
  let phaseProgressionBonus = 0;
  for (let pass = 0; pass < 2; pass++) {
    gate = calcGate({
      price,
      sm50,
      sm200,
      bb,
      cmf,
      vwap,
      volDiv,
      threeDayRule,
      buyScore: bs,
      pureCrypto,
      range52w,
      weeklyTrend,
      volBreakout,
      stopPrice,
      earningsDaysAway: earningsState.daysAway,
    });
    const hardCallsBlocked = (
      price < 1 ||
      (cmf != null && cmf < -0.10) ||
      (bs >= -3 && bs <= 3)
    );
    const hardPutsBlocked = (
      price < 1 ||
      (rsi != null && rsi <= 20) ||
      (cmf != null && cmf > 0.10) ||
      (bs >= -3 && bs <= 3)
    );

    callsSignal = (() => {
      const conditions = [
        { label: "Hard blocks clear (CMF/CHOP)", pass: !hardCallsBlocked, critical: true },
        { label: "Score +7 or higher", pass: bs >= 7, critical: true },
        { label: "⚠ Below VWAP + EMA21 — Calls Blocked", pass: !catchingKnivesCallsBlock, critical: true },
        { label: "BB Squeeze active", pass: !!bb?.isSqueeze, critical: false },
        { label: "No volume exhaustion climax", pass: !volClimax?.blockCalls, critical: true },
        { label: "Price above VWAP", pass: !vwap || price > vwap, critical: true },
        { label: "200 SMA structure OK", pass: !deathCross, critical: true },
        { label: "Golden cross active", pass: goldenCross, critical: false },
        { label: "CMF positive (smart money must confirm)", pass: cmf != null && cmf > 0.05, critical: true },
        { label: "RVOL above average", pass: !!rvol && rvol.ratio >= 1.2, critical: false },
        { label: "Entry gate not blocked", pass: !gate.blocked, critical: true },
        { label: "3-day rule clear", pass: !threeDayRule?.postDrop?.active && !threeDayRule?.earningsGap?.active, critical: true },
        { label: "No OBV bearish divergence", pass: !obv?.bearishDiv, critical: true },
        { label: "Williams %R not overbought", pass: !williamsR?.overbought, critical: false },
      ];
      const criticalFails = conditions.filter(c => c.critical && !c.pass);
      const passed = conditions.filter(c => c.pass).length;
      const allCriticalPass = criticalFails.length === 0;
      const fullSignal = allCriticalPass && passed >= 8;
      const partialSignal = allCriticalPass && passed >= 6;
      return { conditions, passed, total: conditions.length, criticalFails, allCriticalPass, fullSignal, partialSignal };
    })();

    putsSignal = (() => {
      const shortLevelConviction = bs <= -5 && deathCross;
      const cmfValidForPuts = cmf != null && (shortLevelConviction ? cmf < 0.05 : cmf < -0.05);
      const rsiValidForPuts = rsi != null && (
        rsi > 55 ||
        (rsi > 45 && rsiDiv?.bearish) ||
        (rsi < 40 && deathCross)
      );
      const conditions = [
        { label: "Hard blocks clear (RSI/CMF/CHOP)", pass: !hardPutsBlocked, critical: true },
        { label: "Score -4 or lower", pass: bs <= -4, critical: true },
        { label: "RSI not at extreme (> 20)", pass: rsi == null || rsi > 20, critical: true },
        { label: "200 SMA breakdown OR bearish score -5+", pass: deathCross || bs <= -5, critical: true },
        { label: "CMF not accumulating", pass: cmfValidForPuts, critical: true },
        { label: "Price below VWAP", pass: !!vwap && price < vwap, critical: false },
        { label: "RSI confirms bearish bias", pass: rsiValidForPuts, critical: false },
        { label: "3+ sell conditions active", pass: sellCount >= 3 || shortLevelConviction, critical: true },
        { label: "No golden cross blocking puts", pass: !goldenCross, critical: false },
        { label: "OBV bearish or diverging", pass: !!obv && (!obv.rising || obv.bearishDiv), critical: false },
        { label: "3-day rule clear", pass: !threeDayRule?.postDrop?.active && !threeDayRule?.earningsGap?.active, critical: false },
        { label: "RVOL confirms down move", pass: !!rvol && rvol.ratio >= 1.1, critical: false },
      ];
      const criticalFails = conditions.filter(c => c.critical && !c.pass);
      const passed = conditions.filter(c => c.pass).length;
      const allCriticalPass = criticalFails.length === 0;
      const fullSignal = allCriticalPass && passed >= 8;
      const partialSignal = allCriticalPass && passed >= 6;
      return { conditions, passed, total: conditions.length, criticalFails, allCriticalPass, fullSignal, partialSignal };
    })();

    const phase3Momentum =
      (callsSignal.allCriticalPass && callsSignal.partialSignal) ||
      (putsSignal.allCriticalPass && putsSignal.partialSignal);
    if (pass === 0 && earlyWarning.phase1Any && earlyWarning.phase2Any && phase3Momentum) {
      phaseProgressionBonus = 1;
      bs = Math.round((bs + 1) * 2) / 2;
      bs = Math.max(-15, Math.min(15, bs));
      earlyWarning.progressionNote = "📈 CONFIRMED — Phase 1→2→3 progression detected";
    }
  }
  buyScore = bs;
  const momentumBeforeDeadCat = bs;

  let deadCatBounce = null;
  let deadCatStoryOnly = "";
  if (!pureCrypto && raw.deadCatBounce?.eligible === true && momentumBeforeDeadCat < 6) {
    const dcb = raw.deadCatBounce;
    let bonus = typeof dcb.scoreBonusProvisional === "number" ? dcb.scoreBonusProvisional : 0;
    const warnings = [];
    let blocked = false;
    if (typeof tideScore === "number" && tideScore < -2) {
      bonus = 0;
      blocked = true;
      warnings.push("tide");
    }
    if (cmf != null && cmf < -0.15) {
      bonus = Math.max(0, bonus - 2);
      warnings.push("cmf_penalty");
    }
    if (rsi != null && rsi > 40 && dcb.phase1) {
      bonus = Math.max(0, bonus - 2);
      warnings.push("rsi_phase1_block");
    }
    buyScore = Math.round((buyScore + bonus) * 2) / 2;
    buyScore = Math.max(-15, Math.min(15, buyScore));
    deadCatBounce = {
      ...dcb,
      appliedBonus: bonus,
      blocked,
      warnings,
    };
    deadCatStoryOnly = buildDeadCatPriceStory(symbol || "", deadCatBounce, earningsState.daysAway);
  }

  const tradeSetupMerge = applyTradeSetupsMerge(raw, {
    buyScore,
    tideScore,
    cmf,
    rsi,
    earnDays: earningsState.daysAway,
    momentumBeforeDeadCat,
    deadCatBounce,
    pureCrypto,
    rawDcb: raw.deadCatBounce,
    price,
    range52w,
    symbol: symbol || "",
    sm200,
    ema21,
  });
  buyScore = tradeSetupMerge.buyScore;
  let activeSetupType = tradeSetupMerge.activeSetupType;
  const measurementScores = computeMeasurementScoreBonus(consolidation, ema8, ema20, emaAlignment, relativeStrength);
  buyScore = Math.round((buyScore + measurementScores.bonus) * 2) / 2;
  if (activeSetupType === "MOMENTUM" && deadCatBounce && deadCatStoryOnly) {
    activeSetupType = "DEAD_CAT_BOUNCE";
  }

  const confluence = evaluateConfluence({
    closes,
    highs,
    lows,
    volumes,
    price,
    fibSwing: fibRetracement.fibSwing ?? raw?.fibSwing ?? null,
    fibBounce: fibRetracement.fibBounce ?? raw?.fibBounce ?? false,
    rsiDiv,
  });
  addonRunning += buyScore - buyScoreAfterTideMultipliers + (confluence.bonus ?? 0);
  buyScore = clampScore(
    Math.round(
      applyAddonCap(scoringV2Core.buyScoreCore, addonRunning) *
        weeklyTrendMultiplier *
        tideMultiplier *
        2
    ) / 2
  );
  if (confluence.setupType && !activeSetupType) activeSetupType = confluence.setupType;

  const defaultPriceCtx = symbol
    ? `${String(symbol).toUpperCase()} — 52-week range score ${range52w?.score != null ? `${range52w.score}/100` : "—"}; use the headline score and gate for overall stance.`
    : "";
  const priceStoryParagraph = buildUnifiedPriceStory(
    symbol || "",
    deadCatStoryOnly,
    tradeSetupMerge,
    defaultPriceCtx,
  );

  let callsSignalOut = callsSignal;
  if (
    raw.deadCatBounce?.mutual_exclusion?.suppress_standard_momentum_calls_display
    && momentumBeforeDeadCat < 6
  ) {
    callsSignalOut = {
      ...callsSignal,
      fullSignal: false,
      partialSignal: false,
      bouncePlayOnly: true,
      bouncePlayLabel: raw.deadCatBounce.mutual_exclusion.bounce_play_label
        || "BOUNCE PLAY — different from standard momentum entry",
      suppressedStandardMomentumCalls: true,
    };
  }

  const exits = calcExits({ price, rsi, macd, bb, cmf, volDiv, rsiDiv, sellCount, atrStop2x });

  const breakout52w = calc52wBreakoutSetup({
    range52w,
    rvol,
    cmf,
    buyScore,
    rsi,
    counterTrendWeeklyCut,
  });

  const extended = bb && price > bb.upper;
  const accumZoneSignal = (() => {
    const a52w = range52w?.score != null && range52w.score <= 25;
    const aRSI = rsi != null && rsi <= 38;
    const aCMF = cmf != null && cmf >= -0.08;
    const aScore = buyScore >= -6;
    const aDiv = rsiDiv?.bullish === true;
    const aVol = volDiv?.bearish === false;
    return a52w && aRSI && aCMF && aScore && (aDiv || aVol);
  })();
  const signal = deathCross ? (buyScore >= 0 ? "Below Key Average" : "Broken Structure")
    : accumZoneSignal ? "ACCUM ZONE"
    : extended ? "WAIT — EXTENDED"
    : buyScore >= 12 ? "POWER PLAY"
    : buyScore >= 6  ? "STANDARD LONG"
    : buyScore >= 4  ? "SPECULATIVE"
    : buyScore <= -12 ? "BREAKDOWN"
    : buyScore <= -6  ? "STANDARD SHORT"
    : buyScore <= -4  ? "SCALP ONLY"
    : (buyScore >= -3 && buyScore <= 3) ? "CHOP"
    : buyScore >= 2  ? "BUY"
    : buyScore <= -2 ? "SELL"
    : "HOLD";
  const sellSignal = sellCount >= 4 ? "SELL NOW" : sellCount >= 3 ? "TRIM" : sellCount >= 2 ? "CAUTION" : sellCount >= 1 ? "WATCH" : "HOLD";

  // ── 2-YEAR CALMAR RATIO ──────────────────────────────────────────────────
  // Uses full close history available (up to 2 years / 500 bars)
  // Annualized return = (current / oldest close)^(252/nDays) - 1
  // Max drawdown = largest peak-to-trough drop over the full period
  const calmar2y = (() => {
    if (closes.length < 63) return null; // need at least 3 months
    const n = closes.length; // reversed: closes[0] = today, closes[n-1] = oldest
    const rev = [...closes].reverse(); // chronological: rev[0] = oldest, rev[n-1] = today
    // Annualized return over full period
    const totalReturn = (rev[n - 1] - rev[0]) / rev[0];
    const years = n / 252;
    const annReturn = (Math.pow(1 + totalReturn, 1 / years) - 1) * 100;
    // Max drawdown — scan chronologically for peak-to-trough
    let peak = rev[0], maxDD = 0;
    for (let i = 1; i < n; i++) {
      if (rev[i] > peak) peak = rev[i];
      const dd = (peak - rev[i]) / peak * 100;
      if (dd > maxDD) maxDD = dd;
    }
    if (maxDD < 3) return null; // no meaningful drawdown to measure against
    const ratio = annReturn / maxDD;
    return Math.min(Math.max(ratio, -9.9), 9.9); // cap ±9.9
  })();

  const scoreBreakdown = {
    core: {
      macdCore,
      rsiCore,
      cmfCore,
      rsSpyCore,
      bbCore,
      sectorRotationCore,
      rsiDivScore,
      keyLevelBonus,
      breakout52wScore,
      obvDivScore,
      fibonacciScore: fibRetracement.fibLevel != null ? fibRetracement.fibScore : null,
      fibLevel: fibRetracement.fibLevel,
      fibBounce: fibRetracement.fibBounce,
      confluenceScore: confluence.bonus ?? 0,
      trendCore,
      momentumCore,
      breakoutCore,
      addonKeepers: addonBeforeWeekly,
      scoringVersion: "v2",
      subtotal: buyScoreBeforeWeekly,
    },
    phaseSystem: {
      ...(earlyWarning?.breakdown || {}),
      phaseProgressionBonus: phaseProgressionBonus ?? 0,
      subtotal: (earlyWarning?.phaseScore ?? 0) + (phaseProgressionBonus ?? 0),
    },
    setup: {
      activeSetupType: activeSetupType || null,
      setupBonus: tradeSetupMerge?.setupBonus ?? 0,
      setupLabels: tradeSetupMerge?.setupLabels || [],
    },
    measurements: {
      consolidation: measurementScores.breakdown.consolidation,
      ema: measurementScores.breakdown.ema,
      rs: measurementScores.breakdown.rs,
      subtotal: measurementScores.bonus,
    },
    tiers: (() => {
      const structure = (measurementScores.breakdown.ema ?? 0) + (measurementScores.breakdown.consolidation ?? 0);
      const volume = cmfCore + obvDivScore;
      const fibonacci = fibRetracement.fibLevel != null ? (fibRetracement.fibScore ?? 0) : 0;
      const donchian = breakout52wScore + bbCore;
      const momentumRsi = rsiCore;
      const confluenceTier = confluence?.active ? (confluence.bonus ?? 0) : 0;
      const parts = [structure, volume, fibonacci, donchian, momentumRsi, confluenceTier];
      const rawTotal = Math.round(parts.reduce((s, v) => s + v, 0) * 2) / 2;
      return { structure, volume, fibonacci, donchian, momentumRsi, confluence: confluenceTier, rawTotal };
    })(),
    multipliers: {
      buyScoreBeforeWeekly,
      weeklyTrendMultiplier,
      weeklyBearishTideOverrideLabel,
      buyScoreAfterWeekly: Math.round(buyScoreBeforeWeekly * weeklyTrendMultiplier * 2) / 2,
      buyScoreBeforeTide,
      tideMultiplier,
      tideBand,
      breadthScore: marketTide?.breadthScore ?? null,
      buyScoreAfterTide: Math.round(buyScoreBeforeTide * tideMultiplier * 2) / 2,
      sectorEtf: rsVsSector?.etf || null,
      sectorLabel: rsVsSector?.label || null,
    },
  };

  const momentumSignal = signal;
  const moveMetrics = computeMoveMetrics(closes);
  const ema20Val = ema20?.value ?? null;
  const ema8Val = ema8?.value ?? null;
  const postMoveAssessment = generatePostMoveAssessment(moveMetrics, {
    ema20Val,
    ema8Val,
    t1,
    verdict: raw?.verdict,
    signal: raw?.signal ?? momentumSignal,
    action: raw?.action,
    buyScore,
    rvolRatio: rvol?.ratio,
    price,
    inPosition: raw?.inPosition,
  });

  const divScoreBefore = rsiDiv?.bullish ? 2 : rsiDiv?.bearish ? -2 : 0;
  rsiDiv = calcRSIDiv(closes, highs, lows, {
    volumes,
    cmf,
    price,
    priorBuyScore: priorBuyScore != null ? Number(priorBuyScore) : null,
    currentBuyScore: buyScore,
  });
  const divScoreAfter = rsiDiv?.bullish ? 2 : rsiDiv?.bearish ? -2 : 0;
  if (divScoreAfter !== divScoreBefore) {
    buyScore = clampScore(Math.round((buyScore + divScoreAfter - divScoreBefore) * 2) / 2);
  }

  const hv20 = raw?.hv20 ?? raw?.historicalVol20 ?? null;
  const optionsChecks = evaluateOptionsChecks({
    symbol: sym || symbol,
    earningsDaysAway: earningsState.daysAway,
    chainIv30: raw?.chainIv30 ?? raw?.atmIv ?? null,
    hv20,
    ivRank: raw?.ivRank ?? null,
    skipLocalIvSave: true,
  });
  gate = calcGate({
    price,
    sm50,
    sm200,
    bb,
    cmf,
    vwap,
    volDiv,
    threeDayRule,
    buyScore,
    pureCrypto,
    range52w,
    weeklyTrend,
    volBreakout,
    stopPrice,
    earningsDaysAway: earningsState.daysAway,
  });

  const analysisResult = {
    price, pctChange, sm50, sm200, ema21, ema9, nineEmaEntry, ema9AboveEma21, ema21SlopePct5, ema21SlopeLabel, rsi, macd, bb, cmf, cmfDiv, vwap, goldenCross, deathCross, buyScore, buyScoreBeforeWeekly, weeklyTrendMultiplier, counterTrendWeeklyCut, weeklyBearishTideOverrideLabel, buyScoreBeforeTide, tideMultiplier, marketTide, tideBand, stock10Return, rsVsSpy10, downVsUpVol, breakout52w, sellFlags, sellCount, sellSignal,
    signal: (typeof raw?.signal === "string" && raw.signal) ? raw.signal : momentumSignal,
    momentumSignal,
    verdict: raw?.verdict ?? null,
    action: raw?.action ?? null,
    signalStrength: raw?.signalStrength ?? null,
    signalReasons: raw?.signalReasons ?? null,
    module1: raw?.module1 ?? null,
    module2: raw?.module2 ?? null,
    module3: raw?.module3 ?? null,
    module4: raw?.module4 ?? null,
    move1d: moveMetrics.move1d,
    move5d: moveMetrics.move5d,
    move21d: moveMetrics.move21d,
    move63d: moveMetrics.move63d,
    pctFromRecentHigh: moveMetrics.pctFromRecentHigh,
    pctFromRecentLow: moveMetrics.pctFromRecentLow,
    recentHigh20: moveMetrics.recentHigh20,
    recentLow20: moveMetrics.recentLow20,
    postMoveAssessment,
    t1, t2, t1Source, t2Source, d1, d2, recentHigh, atr, stopPrice, tradeStop: stopPrice, rr1, rr2, rr1BelowTarget, target_basis, keyLevelProximity, volDiv, fibs, pivots, rsiDiv, rsiBullishDivergence: rsiDiv?.rsiBullishDivergence === true, rsiBearishDivergence: rsiDiv?.rsiBearishDivergence === true, rsiDivergenceContext: rsiDiv?.rsiDivergenceContext ?? null, range52w, threeDayRule, gate, exits, adx, stoch, obv, psar, rvol, trendStructure, williamsR, volBreakout, volClimax, consolidation, ema8, ema20, emaAlignment, relativeStrength, measurementScoreBreakdown: measurementScores.breakdown, wrRecovery, callsSignal: callsSignalOut, putsSignal, rs, rsVsSector, weeklyTrend, signalChecks, bullCount, bearCount, earningsState, earningsDaysAway: earningsState.daysAway, earningsNextDate: earningsState.nextDateIso ?? null, scanEarningsDaysAway: earningsState.daysAway, calmar2y, catchingKnivesCallsBlock, earlyWarning, phaseProgressionBonus, deadCatBounce, priceStoryParagraph, momentumBeforeDeadCat, activeSetupType, tradeSetups: tradeSetupMerge.tradeSetups, gates, scoreBreakdown, fibLevel: fibRetracement.fibLevel, fibBounce: fibRetracement.fibBounce, fibScore: fibRetracement.fibScore, confluence, confluenceScore: confluence.bonus ?? 0, closes, highs, lows, opens: raw?.opens, volumes, phaseContext, symbol: sym || symbol || "",
    scoringVersion: "v2",
    ...optionsChecks,
    earnings_days: optionsChecks.earningsDays,
    iv_hv_ratio: optionsChecks.ivHvRatio,
    options_flag: optionsChecks.optionsFlag,
    module_signal: raw?.signal ?? momentumSignal,
  };
  attachScanLanes(analysisResult, ctx);
  attachScannerAction(analysisResult);
  attachMovingAverages(analysisResult);
  assertGateScoreConsistency(analysisResult);
  return attachPrimeSetup(analysisResult);
}

export function runAnalysisV2(raw, ctx = {}) {
  if (!ctx.tickerSectorMap) ctx.tickerSectorMap = {};
  const symbol = ctx.symbol ?? raw?.symbol ?? null;
  const cryptoMode = !!ctx.cryptoMode;
  const marketTide = ctx.marketTide ?? null;
  const spyReturn = ctx.spyReturn ?? null;
  return runAnalysisFull(raw, ctx);
}
